import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RolesByType } from '../../modules/dlo/components/DLOFilterByRoleModal';
import { DayItem } from '../../modules/dlo/components/DLOFilterByDayModal';
import { 
  fetchKCPHData,
  fetchDashboardHours,
  fetchDashboardSchedule,
  fetchDSDZoneVolumeDistributionByDay, 
  fetchNONDSDZoneVolumeDistributionByDay,
  fetchDSDDailyPercentage,
  fetchNONDSDDailyPercentage,
  fetchShiftTimes,
  fetchHistoricalHours,
  fetchScheduleSettingsForecastDaily,
  fetchScheduleSettingsForecastDailyNONDSD,
  fetchScheduleSettingsActualDaily,
  fetchScheduleSettingsActualDailyNONDSD,
  fetchScheduleSettingsForecastZoneDistributionDSD,
  fetchScheduleSettingsForecastZoneDistributionNONDSD,
  fetchScheduleSettingsActualZoneDistributionDSD,
  fetchScheduleSettingsActualZoneDistributionNONDSD,
  fetchScheduleSettingsHistoricalHours,
  updateScheduleSettingsForecastDaily,
  updateScheduleSettingsActualDaily,
  updateScheduleSettingsForecastZoneDistribution,
  updateScheduleSettingsActualZoneDistribution,
  updateScheduleSettingsHistoricalHours,
  updateLocationSettingsHistoricalHours,
  fetchScheduleForecast,
  cleanScheduleSettings,
  fetchCreateScheduleHours,
  fetchActualHours,
  fetchScheduleEmployeeData,
  fetchEmployeeRoles,
  fetchAllEmployeeRoles,
  fetchEmployees,
  copySchedule,
  saveScheduleData,
  submitScheduleStatus
} from '../thunks/dloThunks';

export interface KCPHData {
  actualKcph: number | null;
  targetKcph: number | null;
  forecastedKcph: number | null;
  scheduledKcph: number | null;
  estimatedKcph: number | null;
  adjustedRecommendedKcph: number | null;
  crewedKcph: number | null;
}

export interface DashboardScheduleRecord {
  "Loc ID": string;
  "YR": number;
  "PRD": number;
  "WK": number;
  "is_fav": boolean;
  "fav_name": string | null;
  "total_forecasted_cases": number | null;
  "total_scheduled_hours": number;
  "total_actual_hours": number;
  "recommended_hours": number | null;
  "CreatedAt": string;
  "CreatedBy": string | null;
  "ModifiedAt": string | null;
  "ModifiedBy": string | null;
}

export interface DashboardScheduleData {
  success: boolean;
  message: string;
  data: DashboardScheduleRecord[];
  summary: {
    totalRecords: number;
    totalForecastedCases: number;
    totalScheduledHours: number;
    totalActualHours: number;
    totalRecommendedHours: number;
  };
  filters: {
    locationId: string;
  };
}

// Centralized Error Handling Types
export interface DLOError {
  id: string;
  apiName: string;
  message: string;
  timestamp: number;
  context?: any;
}

export interface CentralizedErrorState {
  errors: DLOError[];
  activeError: DLOError | null;
  isErrorModalVisible: boolean;
}

// API Names for consistent error tracking
export const DLO_API_NAMES = {
  FETCH_DASHBOARD_SCHEDULE: 'fetchDashboardSchedule',
  FETCH_KCPH_DATA: 'fetchKCPHData',
  FETCH_DASHBOARD_HOURS: 'fetchDashboardHours',
  FETCH_DSD_ZONE_VOLUME: 'fetchDSDZoneVolumeDistributionByDay',
  FETCH_NON_DSD_ZONE_VOLUME: 'fetchNONDSDZoneVolumeDistributionByDay',
  FETCH_DSD_DAILY_PERCENTAGE: 'fetchDSDDailyPercentage',
  FETCH_NON_DSD_DAILY_PERCENTAGE: 'fetchNONDSDDailyPercentage',
  FETCH_SHIFT_TIMES: 'fetchShiftTimes',
  FETCH_HISTORICAL_HOURS: 'fetchHistoricalHours',
  FETCH_SCHEDULE_SETTINGS_FORECAST_DAILY: 'fetchScheduleSettingsForecastDaily',
  FETCH_SCHEDULE_SETTINGS_FORECAST_DAILY_NON_DSD: 'fetchScheduleSettingsForecastDailyNONDSD',
  FETCH_SCHEDULE_SETTINGS_ACTUAL_DAILY: 'fetchScheduleSettingsActualDaily',
  FETCH_SCHEDULE_SETTINGS_ACTUAL_DAILY_NON_DSD: 'fetchScheduleSettingsActualDailyNONDSD',
  FETCH_SCHEDULE_SETTINGS_FORECAST_ZONE_DSD: 'fetchScheduleSettingsForecastZoneDistributionDSD',
  FETCH_SCHEDULE_SETTINGS_FORECAST_ZONE_NON_DSD: 'fetchScheduleSettingsForecastZoneDistributionNONDSD',
  FETCH_SCHEDULE_SETTINGS_ACTUAL_ZONE_DSD: 'fetchScheduleSettingsActualZoneDistributionDSD',
  FETCH_SCHEDULE_SETTINGS_ACTUAL_ZONE_NON_DSD: 'fetchScheduleSettingsActualZoneDistributionNONDSD',
  FETCH_SCHEDULE_SETTINGS_HISTORICAL_HOURS: 'fetchScheduleSettingsHistoricalHours',
  UPDATE_SCHEDULE_SETTINGS_FORECAST_DAILY: 'updateScheduleSettingsForecastDaily',
  UPDATE_SCHEDULE_SETTINGS_ACTUAL_DAILY: 'updateScheduleSettingsActualDaily',
  UPDATE_SCHEDULE_SETTINGS_FORECAST_ZONE: 'updateScheduleSettingsForecastZoneDistribution',
  UPDATE_SCHEDULE_SETTINGS_ACTUAL_ZONE: 'updateScheduleSettingsActualZoneDistribution',
  UPDATE_SCHEDULE_SETTINGS_HISTORICAL_HOURS: 'updateScheduleSettingsHistoricalHours',
  UPDATE_LOCATION_SETTINGS_HISTORICAL_HOURS: 'updateLocationSettingsHistoricalHours',
  FETCH_SCHEDULE_FORECAST: 'fetchScheduleForecast',
  CLEAN_SCHEDULE_SETTINGS: 'cleanScheduleSettings',
  FETCH_CREATE_SCHEDULE_HOURS: 'fetchCreateScheduleHours',
  FETCH_ACTUAL_HOURS: 'fetchActualHours',
  FETCH_SCHEDULE_EMPLOYEE_DATA: 'fetchScheduleEmployeeData',
  FETCH_EMPLOYEE_ROLES: 'fetchEmployeeRoles',
  FETCH_EMPLOYEES: 'fetchEmployees',
  SAVE_SCHEDULE_DATA: 'saveScheduleData',
  SUBMIT_SCHEDULE_STATUS: 'submitScheduleStatus',
  COPY_SCHEDULE: 'copySchedule',
  UPDATE_SHIFT_TIMES: 'updateShiftTimes'
} as const;

export interface DashboardHoursDay {
  locationId: string;
  locationName: string;
  year: number;
  period: number;
  week: number;
  dayNumber: number;
  totalForecastHours: number;
  totalScheduledHours: number;
  totalActualHours: number;
  variance: number;
  actualVariance: number;
}

export interface DashboardHoursData {
  data: DashboardHoursDay[];
  summary: {
    totalDays: number;
    totalForecastHours: number;
    totalScheduledHours: number;
    totalActualHours: number;
  };
  totalRecords: number;
  filters: {
    locationId: string;
    year: number;
    period: number;
    week: number;
  };
}

// Centralized Error Handling Types
export interface DLOError {
  id: string;
  apiName: string;
  message: string;
  timestamp: number;
  context?: any;
}

export interface CentralizedErrorState {
  errors: DLOError[];
  activeError: DLOError | null;
  isErrorModalVisible: boolean;
}

// Session Lock State
export interface DLOState {
  sessionLock: any;
  // Centralized Error Handling
  centralizedErrors: CentralizedErrorState;
  
  dashboard: {
    // Add dashboard-specific state here
    loading: boolean;
    error: string | null;
    data: any;
    // KCPH related state
    kcphData: KCPHData | null;
    kcphLoading: boolean;
    kcphError: string | null;
    // Dashboard hours related state
    hoursData: DashboardHoursData | null;
    hoursLoading: boolean;
    hoursError: string | null;
    // Dashboard schedule related state
    scheduleData: DashboardScheduleData | null;
    scheduleLoading: boolean;
    scheduleError: string | null;
    // Dashboard filtered schedule related state
    filteredScheduleData: DashboardScheduleData | null;
    filteredScheduleLoading: boolean;
    filteredScheduleError: string | null;
  };
  locationSetting: {
    // Add location setting-specific state here
    loading: boolean;
    error: string | null;
    DSDVolumeDistributionByDay: any;
    NONDSDVolumeDistributionByDay: any;
    DSDDailyPercentage: any;
    NONDSDDailyPercentage: any;
    shiftTimes: any;
    historicalHours: any;
    // Individual loading states for each API call
    dsdLoading: boolean;
    nonDsdLoading: boolean;
    dsdDailyLoading: boolean;
    nonDsdDailyLoading: boolean;
    shiftTimesLoading: boolean;
    historicalHoursLoading: boolean;
    historicalHoursSaving: boolean;
    // Saved states
    historicalHoursSaved: boolean;
  };
  scheduleSetting: {
    // Add schedule setting-specific state here
    loading: boolean;
    error: string | null;
    // Active context for two-way sync functionality
    activeContext: 'ActualVolumeTab' | 'ForecastVolumeTab' | null;
    scheduleSettingsForecastDaily: any;
    scheduleSettingsForecastDailyNONDSD: any;
    scheduleSettingsActualDaily: any;
    scheduleSettingsActualDailyNONDSD: any;
    // Zone distribution forecast data
    scheduleSettingsForecastZoneDistributionDSD: any;
    scheduleSettingsForecastZoneDistributionNONDSD: any;
    // Zone distribution actual data
    scheduleSettingsActualZoneDistributionDSD: any;
    scheduleSettingsActualZoneDistributionNONDSD: any;
    // Historical hours data
    scheduleSettingsHistoricalHours: any;
    // Individual loading states
    forecastDailyLoading: boolean;
    forecastDailyNONDSDLoading: boolean;
    actualDailyLoading: boolean;
    actualDailyNONDSDLoading: boolean;
    // Zone distribution loading states
    forecastZoneDistributionDSDLoading: boolean;
    forecastZoneDistributionNONDSDLoading: boolean;
    actualZoneDistributionDSDLoading: boolean;
    actualZoneDistributionNONDSDLoading: boolean;
    // Historical hours loading state
    scheduleSettingsHistoricalHoursLoading: boolean;
    // Historical hours update loading state
    historicalHoursUpdateLoading: boolean;
    // Update loading states
    updateLoading: boolean;
    updateSuccess: boolean;
    // Individual update loading states for each table
    forecastDSDUpdateLoading: boolean;
    forecastNONDSDUpdateLoading: boolean;
    actualDSDUpdateLoading: boolean;
    actualNONDSDUpdateLoading: boolean;
    // Zone distribution update loading states
    forecastDSDZoneUpdateLoading: boolean;
    forecastNONDSDZoneUpdateLoading: boolean;
    actualDSDZoneUpdateLoading: boolean;
    actualNONDSDZoneUpdateLoading: boolean;
    // Zone distribution saved states
    forecastDSDZoneSaved: boolean;
    forecastNONDSDZoneSaved: boolean;
    actualDSDZoneSaved: boolean;
    actualNONDSDZoneSaved: boolean;
    // Table data states to persist user input across tab switches
    forecastDSDTableData: any[];
    forecastNONDSDTableData: any[];
    actualDSDTableData: any[];
    actualNONDSDTableData: any[];
    // Zone distribution table data states
    forecastDSDZoneTableData: any[];
    forecastNONDSDZoneTableData: any[];
    actualDSDZoneTableData: any[];
    actualNONDSDZoneTableData: any[];
    // Historical hours table data state
    historicalHoursTableData: any[];
    // Historical hours unsaved changes tracking
    historicalHoursUnsavedChanges: Record<string, Record<string, boolean>>;
    // Shift times data and state
    shiftTimes: any;
    shiftTimesLoading: boolean;
    shiftTimesTableData: any[];
    shiftTimesUpdateLoading: boolean;
    // Schedule forecast data and state
    scheduleForecast: any;
    scheduleForecastLoading: boolean;
    scheduleForecastTableData: any[];
    scheduleForecastError: string | null;
    // Clean schedule settings data and state
    cleanScheduleSettingsData: any;
    cleanScheduleSettingsLoading: boolean;
    cleanScheduleSettingsError: string | null;
  };
  createSchedule: {
    // Add create schedule-specific state here
    loading: boolean;
    error: string | null;
    scheduleData: any;
    // Period, Week, and Year for Copy Schedule functionality
    period: number | null;
    week: number | null;
    year: number | null;
  // Copy Schedule states
  copyScheduleLoading: boolean;
  copyScheduleError: string | null;
    // Schedule hours data and loading state
    scheduleHours: any;
    scheduleHoursLoading: boolean;
    scheduleHoursError: string | null;
    // Employee rows management (for Schedule Hours)
    employeeRows: any[];
    deletedEmployeeRows: any[];
    nextEmployeeRowId: number;
    // Role filtering state
    filteredRoles: RolesByType;
    // Employee roles data and loading state
    employeeRoles: any;
    employeeRolesLoading: boolean;
    employeeRolesError: string | null;
    // All LO roles (including disabled) — used when loading existing schedules
    allEmployeeRoles: any;
    // Employees data and loading state
    employees: any;
    employeesLoading: boolean;
    employeesError: string | null;
    // Schedule employee data and loading state
    scheduleEmployeeData: any;
    scheduleEmployeeDataLoading: boolean;
    scheduleEmployeeDataError: string | null;
    // Raw initial schedules from GET /dlo/schedule (unfiltered, for orphan detection)
    initialSchedules: any;
    // Save operation states
    saveLoading: boolean;
    saveError: string | null;
    saveSuccess: boolean;
    // Change tracking
    hasUnsavedChanges: boolean;
    // Submit operation states
    submitLoading: boolean;
    submitError: string | null;
    submitSuccess: boolean;
    submitResponse: any;
    // Day filter state
    selectedDays: string[];
    dayFilter: DayItem[];
  };
  createActualHours: {
    // Dedicated state for Actual Hours tab
    loading: boolean;
    error: string | null;
    actualHoursData: any;
    // Period, Week, and Year for Actual Hours functionality
    period: number | null;
    week: number | null;
    year: number | null;
    // Actual hours data and loading state
    actualHours: any;
    actualHoursLoading: boolean;
    actualHoursError: string | null;
    // Employee rows management (for Actual Hours)
    actualEmployeeRows: any[];
    actualDeletedEmployeeRows: any[];
    nextActualEmployeeRowId: number;
    // Role filtering state for Actual Hours
    actualFilteredRoles: RolesByType;
    // Day filter state for Actual Hours
    actualSelectedDays: string[];
    actualDayFilter: DayItem[];
  };
}

const initialState: DLOState = {
  // Centralized Error Handling
  centralizedErrors: {
    errors: [],
    activeError: null,
    isErrorModalVisible: false,
  },

  dashboard: {
    loading: false,
    error: null,
    data: null,
    kcphData: null,
    kcphLoading: false,
    kcphError: null,
    hoursData: null,
    hoursLoading: false,
    hoursError: null,
    scheduleData: null,
    scheduleLoading: false,
    scheduleError: null,
    filteredScheduleData: null,
    filteredScheduleLoading: false,
    filteredScheduleError: null,
  },
  locationSetting: {
    loading: false,
    error: null,
    DSDVolumeDistributionByDay: null,
    NONDSDVolumeDistributionByDay: null,
    DSDDailyPercentage: null,
    NONDSDDailyPercentage: null,
    shiftTimes: null,
    historicalHours: null,
    dsdLoading: false,
    nonDsdLoading: false,
    dsdDailyLoading: false,
    nonDsdDailyLoading: false,
    shiftTimesLoading: false,
    historicalHoursLoading: false,
    historicalHoursSaving: false,
    historicalHoursSaved: false,
  },
  scheduleSetting: {
    loading: false,
    error: null,
    activeContext: null,
    scheduleSettingsForecastDaily: null,
    scheduleSettingsForecastDailyNONDSD: null,
    scheduleSettingsActualDaily: null,
    scheduleSettingsActualDailyNONDSD: null,
    // Zone distribution forecast data
    scheduleSettingsForecastZoneDistributionDSD: null,
    scheduleSettingsForecastZoneDistributionNONDSD: null,
    // Zone distribution actual data
    scheduleSettingsActualZoneDistributionDSD: null,
    scheduleSettingsActualZoneDistributionNONDSD: null,
    // Historical hours data
    scheduleSettingsHistoricalHours: null,
    forecastDailyLoading: false,
    forecastDailyNONDSDLoading: false,
    actualDailyLoading: false,
    actualDailyNONDSDLoading: false,
    // Zone distribution loading states
    forecastZoneDistributionDSDLoading: false,
    forecastZoneDistributionNONDSDLoading: false,
    actualZoneDistributionDSDLoading: false,
    actualZoneDistributionNONDSDLoading: false,
    // Historical hours loading state
    scheduleSettingsHistoricalHoursLoading: false,
    // Historical hours update loading state
    historicalHoursUpdateLoading: false,
    updateLoading: false,
    updateSuccess: false,
    // Individual update loading states for each table
    forecastDSDUpdateLoading: false,
    forecastNONDSDUpdateLoading: false,
    actualDSDUpdateLoading: false,
    actualNONDSDUpdateLoading: false,
    // Zone distribution update loading states
    forecastDSDZoneUpdateLoading: false,
    forecastNONDSDZoneUpdateLoading: false,
    actualDSDZoneUpdateLoading: false,
    actualNONDSDZoneUpdateLoading: false,
    // Zone distribution saved states
    forecastDSDZoneSaved: false,
    forecastNONDSDZoneSaved: false,
    actualDSDZoneSaved: false,
    actualNONDSDZoneSaved: false,
    // Table data states to persist user input across tab switches
    forecastDSDTableData: [],
    forecastNONDSDTableData: [],
    actualDSDTableData: [],
    actualNONDSDTableData: [],
    // Zone distribution table data states
    forecastDSDZoneTableData: [],
    forecastNONDSDZoneTableData: [],
    actualDSDZoneTableData: [],
    actualNONDSDZoneTableData: [],
    // Historical hours table data state
    historicalHoursTableData: [],
    // Historical hours unsaved changes tracking
    historicalHoursUnsavedChanges: {},
    // Shift times data and state
    shiftTimes: null,
    shiftTimesLoading: false,
    shiftTimesTableData: [],
    shiftTimesUpdateLoading: false,
    // Schedule forecast data and state
    scheduleForecast: null,
    scheduleForecastLoading: false,
    scheduleForecastTableData: [],
    scheduleForecastError: null,
    // Clean schedule settings data and state
    cleanScheduleSettingsData: null,
    cleanScheduleSettingsLoading: false,
    cleanScheduleSettingsError: null,
  },
  createSchedule: {
    loading: false,
    error: null,
    scheduleData: null,
    period: null,
    week: null,
    year: null,
    copyScheduleLoading: false,
    copyScheduleError: null,
    scheduleHours: null,
    scheduleHoursLoading: false,
    scheduleHoursError: null,
    employeeRows: [],
    deletedEmployeeRows: [],
    nextEmployeeRowId: 1,
    filteredRoles: {
      DSD: [],
      'NON-DSD': [],
      Retail: [],
      Other: [],
    },
    employeeRoles: null,
    employeeRolesLoading: false,
    employeeRolesError: null,
    allEmployeeRoles: null,
    employees: null,
    employeesLoading: false,
    employeesError: null,
    scheduleEmployeeData: null,
    scheduleEmployeeDataLoading: false,
    scheduleEmployeeDataError: null,
    // Raw initial schedules from GET /dlo/schedule (unfiltered, for orphan detection)
    initialSchedules: null,
    // Save operation states
    saveLoading: false,
    saveError: null,
    saveSuccess: false,
    // Change tracking
    hasUnsavedChanges: false,
    // Submit operation states
    submitLoading: false,
    submitError: null,
    submitSuccess: false,
    submitResponse: null,
    selectedDays: [],
    dayFilter: [
      { id: '0', name: 'Sunday', enabled: true, disabled: false },
      { id: '1', name: 'Monday', enabled: true, disabled: false },
      { id: '2', name: 'Tuesday', enabled: true, disabled: false },
      { id: '3', name: 'Wednesday', enabled: true, disabled: false },
      { id: '4', name: 'Thursday', enabled: true, disabled: false },
      { id: '5', name: 'Friday', enabled: true, disabled: false },
      { id: '6', name: 'Saturday', enabled: true, disabled: false },
    ],
  },
  createActualHours: {
    loading: false,
    error: null,
    actualHoursData: null,
    period: null,
    week: null,
    year: null,
    actualHours: null,
    actualHoursLoading: false,
    actualHoursError: null,
    actualEmployeeRows: [],
    actualDeletedEmployeeRows: [],
    nextActualEmployeeRowId: 1,
    actualFilteredRoles: {
      DSD: [],
      'NON-DSD': [],
      Retail: [],
      Other: [],
    },
    actualSelectedDays: [],
    actualDayFilter: [
      { id: '0', name: 'Sunday', enabled: true, disabled: false },
      { id: '1', name: 'Monday', enabled: true, disabled: false },
      { id: '2', name: 'Tuesday', enabled: true, disabled: false },
      { id: '3', name: 'Wednesday', enabled: true, disabled: false },
      { id: '4', name: 'Thursday', enabled: true, disabled: false },
      { id: '5', name: 'Friday', enabled: true, disabled: false },
      { id: '6', name: 'Saturday', enabled: true, disabled: false },
    ],
  },
  sessionLock: undefined
};

const dloSlice = createSlice({
  name: 'dlo',
  initialState,
  reducers: {
    // Centralized Error Handling Actions
    addCentralizedError(state, action: PayloadAction<DLOError>) {
      state.centralizedErrors.errors.push(action.payload);
      // Keep only the last 10 errors to prevent memory issues
      if (state.centralizedErrors.errors.length > 10) {
        state.centralizedErrors.errors = state.centralizedErrors.errors.slice(-10);
      }
    },
    setActiveError(state, action: PayloadAction<DLOError | null>) {
      state.centralizedErrors.activeError = action.payload;
    },
    showErrorModal(state, action: PayloadAction<DLOError>) {
      state.centralizedErrors.activeError = action.payload;
      state.centralizedErrors.isErrorModalVisible = true;
    },
    hideErrorModal(state) {
      state.centralizedErrors.isErrorModalVisible = false;
      state.centralizedErrors.activeError = null;
    },
    clearAllErrors(state) {
      state.centralizedErrors.errors = [];
      state.centralizedErrors.activeError = null;
      state.centralizedErrors.isErrorModalVisible = false;
    },
    removeErrorById(state, action: PayloadAction<string>) {
      state.centralizedErrors.errors = state.centralizedErrors.errors.filter(
        error => error.id !== action.payload
      );
      // If the active error was removed, hide the modal
      if (state.centralizedErrors.activeError?.id === action.payload) {
        state.centralizedErrors.activeError = null;
        state.centralizedErrors.isErrorModalVisible = false;
      }
    },

    // Dashboard actions
    setDashboardLoading(state, action: PayloadAction<boolean>) {
      state.dashboard.loading = action.payload;
    },
    setDashboardError(state, action: PayloadAction<string | null>) {
      state.dashboard.error = action.payload;
    },
    setDashboardData(state, action: PayloadAction<any>) {
      state.dashboard.data = action.payload;
    },

    // Filtered Schedule actions
    setFilteredScheduleLoading(state, action: PayloadAction<boolean>) {
      state.dashboard.filteredScheduleLoading = action.payload;
    },
    setFilteredScheduleError(state, action: PayloadAction<string | null>) {
      state.dashboard.filteredScheduleError = action.payload;
    },
    setFilteredScheduleData(state, action: PayloadAction<DashboardScheduleData | null>) {
      state.dashboard.filteredScheduleData = action.payload;
    },
    
    // KCPH actions
    setKCPHLoading(state, action: PayloadAction<boolean>) {
      state.dashboard.kcphLoading = action.payload;
    },
    setKCPHError(state, action: PayloadAction<string | null>) {
      state.dashboard.kcphError = action.payload;
    },
    setKCPHData(state, action: PayloadAction<any>) {
      state.dashboard.kcphData = action.payload;
    },
    
    // Dashboard Hours actions
    setHoursLoading(state, action: PayloadAction<boolean>) {
      state.dashboard.hoursLoading = action.payload;
    },
    setHoursError(state, action: PayloadAction<string | null>) {
      state.dashboard.hoursError = action.payload;
    },
    setHoursData(state, action: PayloadAction<any>) {
      state.dashboard.hoursData = action.payload;
    },

    // Dashboard Schedule actions
    setScheduleLoading(state, action: PayloadAction<boolean>) {
      state.dashboard.scheduleLoading = action.payload;
    },
    setScheduleError(state, action: PayloadAction<string | null>) {
      state.dashboard.scheduleError = action.payload;
    },
    setScheduleData(state, action: PayloadAction<DashboardScheduleData>) {
      state.dashboard.scheduleData = action.payload;
    },

    // Location Setting actions
    setLocationSettingLoading(state, action: PayloadAction<boolean>) {
      state.locationSetting.loading = action.payload;
    },
    setLocationSettingError(state, action: PayloadAction<string | null>) {
      state.locationSetting.error = action.payload;
    },
    setLocationSetting(state, action: PayloadAction<any>) {
      state.locationSetting.DSDVolumeDistributionByDay = action.payload;
    },

    // Schedule Setting actions
    setScheduleSettingLoading(state, action: PayloadAction<boolean>) {
      state.scheduleSetting.loading = action.payload;
    },
    setScheduleSettingError(state, action: PayloadAction<string | null>) {
      state.scheduleSetting.error = action.payload;
    },
    setScheduleSettingsForecastDaily(state, action: PayloadAction<any>) {
      state.scheduleSetting.scheduleSettingsForecastDaily = action.payload;
    },
    setScheduleSettingsForecastDailyNONDSD(state, action: PayloadAction<any>) {
      state.scheduleSetting.scheduleSettingsForecastDailyNONDSD = action.payload;
    },
    // Reset update states
    resetScheduleSettingUpdateState(state) {
      state.scheduleSetting.updateLoading = false;
      state.scheduleSetting.updateSuccess = false;
      state.scheduleSetting.error = null;
    },
    // Reset save states
    resetSaveState(state) {
      state.createSchedule.saveLoading = false;
      state.createSchedule.saveError = null;
      state.createSchedule.saveSuccess = false;
      state.createSchedule.hasUnsavedChanges = false;
    },

    // Reset all schedule setting data
    resetScheduleSettingData(state) {
      state.scheduleSetting.loading = false;
      state.scheduleSetting.error = null;
      state.scheduleSetting.scheduleSettingsForecastDaily = null;
      state.scheduleSetting.scheduleSettingsForecastDailyNONDSD = null;
      state.scheduleSetting.scheduleSettingsActualDaily = null;
      state.scheduleSetting.scheduleSettingsActualDailyNONDSD = null;
      // Zone distribution forecast data
      state.scheduleSetting.scheduleSettingsForecastZoneDistributionDSD = null;
      state.scheduleSetting.scheduleSettingsForecastZoneDistributionNONDSD = null;
      // Zone distribution actual data
      state.scheduleSetting.scheduleSettingsActualZoneDistributionDSD = null;
      state.scheduleSetting.scheduleSettingsActualZoneDistributionNONDSD = null;
      // Historical hours data
      state.scheduleSetting.scheduleSettingsHistoricalHours = null;
      state.scheduleSetting.forecastDailyLoading = false;
      state.scheduleSetting.forecastDailyNONDSDLoading = false;
      state.scheduleSetting.actualDailyLoading = false;
      state.scheduleSetting.actualDailyNONDSDLoading = false;
      // Zone distribution loading states
      state.scheduleSetting.forecastZoneDistributionDSDLoading = false;
      state.scheduleSetting.forecastZoneDistributionNONDSDLoading = false;
      state.scheduleSetting.actualZoneDistributionDSDLoading = false;
      state.scheduleSetting.actualZoneDistributionNONDSDLoading = false;
      // Historical hours loading state
      state.scheduleSetting.scheduleSettingsHistoricalHoursLoading = false;
      // Historical hours update loading state
      state.scheduleSetting.historicalHoursUpdateLoading = false;
      state.scheduleSetting.updateLoading = false;
      state.scheduleSetting.updateSuccess = false;
      // Individual update loading states for each table
      state.scheduleSetting.forecastDSDUpdateLoading = false;
      state.scheduleSetting.forecastNONDSDUpdateLoading = false;
      state.scheduleSetting.actualDSDUpdateLoading = false;
      state.scheduleSetting.actualNONDSDUpdateLoading = false;
      // Zone distribution update loading states
      state.scheduleSetting.forecastDSDZoneUpdateLoading = false;
      state.scheduleSetting.forecastNONDSDZoneUpdateLoading = false;
      state.scheduleSetting.actualDSDZoneUpdateLoading = false;
      state.scheduleSetting.actualNONDSDZoneUpdateLoading = false;
      // Zone distribution saved states
      state.scheduleSetting.forecastDSDZoneSaved = false;
      state.scheduleSetting.forecastNONDSDZoneSaved = false;
      state.scheduleSetting.actualDSDZoneSaved = false;
      state.scheduleSetting.actualNONDSDZoneSaved = false;
      // Table data states to persist user input across tab switches
      state.scheduleSetting.forecastDSDTableData = [];
      state.scheduleSetting.forecastNONDSDTableData = [];
      state.scheduleSetting.actualDSDTableData = [];
      state.scheduleSetting.actualNONDSDTableData = [];
      // Zone distribution table data states
      state.scheduleSetting.forecastDSDZoneTableData = [];
      state.scheduleSetting.forecastNONDSDZoneTableData = [];
      state.scheduleSetting.actualDSDZoneTableData = [];
      state.scheduleSetting.actualNONDSDZoneTableData = [];
      // Historical hours table data state
      state.scheduleSetting.historicalHoursTableData = [];
      // Historical hours unsaved changes tracking
      state.scheduleSetting.historicalHoursUnsavedChanges = {};
      // Shift times data and state
      state.scheduleSetting.shiftTimes = null;
      state.scheduleSetting.shiftTimesLoading = false;
      state.scheduleSetting.shiftTimesTableData = [];
      state.scheduleSetting.shiftTimesUpdateLoading = false;
      // Schedule forecast data and state
      state.scheduleSetting.scheduleForecast = null;
      state.scheduleSetting.scheduleForecastLoading = false;
      state.scheduleSetting.scheduleForecastTableData = [];
      state.scheduleSetting.scheduleForecastError = null;
      // Clean schedule settings data and state
      state.scheduleSetting.cleanScheduleSettingsData = null;
      state.scheduleSetting.cleanScheduleSettingsLoading = false;
      state.scheduleSetting.cleanScheduleSettingsError = null;
    },

    // Table data actions to persist user input across tab switches
    setForecastDSDTableData(state, action: PayloadAction<any[]>) {
      state.scheduleSetting.forecastDSDTableData = action.payload;
    },
    setForecastNONDSDTableData(state, action: PayloadAction<any[]>) {
      state.scheduleSetting.forecastNONDSDTableData = action.payload;
    },
    setActualDSDTableData(state, action: PayloadAction<any[]>) {
      state.scheduleSetting.actualDSDTableData = action.payload;
    },
    setActualNONDSDTableData(state, action: PayloadAction<any[]>) {
      state.scheduleSetting.actualNONDSDTableData = action.payload;
    },

    // Zone distribution table data actions to persist user input across tab switches
    setForecastDSDZoneTableData(state, action: PayloadAction<any[]>) {
      state.scheduleSetting.forecastDSDZoneTableData = action.payload;
      // Reset saved state when data changes
      state.scheduleSetting.forecastDSDZoneSaved = false;
    },
    setForecastNONDSDZoneTableData(state, action: PayloadAction<any[]>) {
      state.scheduleSetting.forecastNONDSDZoneTableData = action.payload;
      // Reset saved state when data changes
      state.scheduleSetting.forecastNONDSDZoneSaved = false;
    },
    setActualDSDZoneTableData(state, action: PayloadAction<any[]>) {
      state.scheduleSetting.actualDSDZoneTableData = action.payload;
      // Reset saved state when data changes
      state.scheduleSetting.actualDSDZoneSaved = false;
    },
    setActualNONDSDZoneTableData(state, action: PayloadAction<any[]>) {
      state.scheduleSetting.actualNONDSDZoneTableData = action.payload;
      // Reset saved state when data changes
      state.scheduleSetting.actualNONDSDZoneSaved = false;
    },

    // Historical hours table data action
    setHistoricalHoursTableData(state, action: PayloadAction<any[]>) {
      state.scheduleSetting.historicalHoursTableData = action.payload;
    },

    // Historical hours unsaved changes tracking action
    setHistoricalHoursUnsavedChanges(state, action: PayloadAction<Record<string, Record<string, boolean>>>) {
      state.scheduleSetting.historicalHoursUnsavedChanges = action.payload;
    },

    // Active context action for two-way sync
    setActiveContext(state, action: PayloadAction<'ActualVolumeTab' | 'ForecastVolumeTab' | null>) {
      state.scheduleSetting.activeContext = action.payload;
    },

    // Location setting historical hours saved state action
    setLocationHistoricalHoursSaved(state, action: PayloadAction<boolean>) {
      state.locationSetting.historicalHoursSaved = action.payload;
    },

    // Shift times actions
    setShiftTimesLoading(state, action: PayloadAction<boolean>) {
      state.scheduleSetting.shiftTimesLoading = action.payload;
    },
    setShiftTimesData(state, action: PayloadAction<any>) {
      state.scheduleSetting.shiftTimes = action.payload;
      state.scheduleSetting.shiftTimesLoading = false;
    },
    setShiftTimesTableData(state, action: PayloadAction<any[]>) {
      state.scheduleSetting.shiftTimesTableData = action.payload;
    },
    setShiftTimesUpdateLoading(state, action: PayloadAction<boolean>) {
      state.scheduleSetting.shiftTimesUpdateLoading = action.payload;
    },
    setShiftTimesError(state, action: PayloadAction<string>) {
      state.scheduleSetting.error = action.payload;
      state.scheduleSetting.shiftTimesLoading = false;
      state.scheduleSetting.shiftTimesUpdateLoading = false;
    },

    // Schedule Forecast actions
    setScheduleForecastLoading(state, action: PayloadAction<boolean>) {
      state.scheduleSetting.scheduleForecastLoading = action.payload;
    },
    setScheduleForecastData(state, action: PayloadAction<any>) {
      state.scheduleSetting.scheduleForecast = action.payload;
      state.scheduleSetting.scheduleForecastLoading = false;
      state.scheduleSetting.scheduleForecastError = null;
    },
    setScheduleForecastTableData(state, action: PayloadAction<any[]>) {
      state.scheduleSetting.scheduleForecastTableData = action.payload;
    },
    setScheduleForecastError(state, action: PayloadAction<string | null>) {
      state.scheduleSetting.scheduleForecastError = action.payload;
      state.scheduleSetting.scheduleForecastLoading = false;
    },

    // Clean Schedule Settings actions
    setCleanScheduleSettingsLoading(state, action: PayloadAction<boolean>) {
      state.scheduleSetting.cleanScheduleSettingsLoading = action.payload;
    },
    setCleanScheduleSettingsData(state, action: PayloadAction<any>) {
      state.scheduleSetting.cleanScheduleSettingsData = action.payload;
      state.scheduleSetting.cleanScheduleSettingsLoading = false;
      state.scheduleSetting.cleanScheduleSettingsError = null;
    },
    setCleanScheduleSettingsError(state, action: PayloadAction<string | null>) {
      state.scheduleSetting.cleanScheduleSettingsError = action.payload;
      state.scheduleSetting.cleanScheduleSettingsLoading = false;
    },

    // Create Schedule actions
    setCreateScheduleLoading(state, action: PayloadAction<boolean>) {
      state.createSchedule.loading = action.payload;
    },
    setCreateScheduleError(state, action: PayloadAction<string | null>) {
      state.createSchedule.error = action.payload;
    },
    setCreateScheduleData(state, action: PayloadAction<any>) {
      state.createSchedule.scheduleData = action.payload;
    },
    // Period, Week, and Year actions for Copy Schedule functionality
    setCreateSchedulePeriodWeek(state, action: PayloadAction<{ period: number; week: number; year: number }>) {
      state.createSchedule.period = action.payload.period;
      state.createSchedule.week = action.payload.week;
      state.createSchedule.year = action.payload.year;
    },
    clearCreateSchedulePeriodWeek(state) {
      state.createSchedule.period = null;
      state.createSchedule.week = null;
      state.createSchedule.year = null;
    },
    // Employee rows management actions
    addEmployeeRow(state) {
      // Check dayFilter to determine which days should be set to "0"
      const dayMapping = {
        '0': 'sunday',
        '1': 'monday', 
        '2': 'tuesday',
        '3': 'wednesday',
        '4': 'thursday',
        '5': 'friday',
        '6': 'saturday'
      };

      const newRow = {
        id: state.createSchedule.nextEmployeeRowId.toString(),
        isSelected: false,
        employee: null,
        role: null,
        shift: null,
        sunday: '',
        monday: '',
        tuesday: '',
        wednesday: '',
        thursday: '',
        friday: '',
        saturday: '',
        total: '',
      };

      // Set disabled days to "0"
      state.createSchedule.dayFilter.forEach(day => {
        if (!day.enabled) {
          const dayField = dayMapping[day.id as keyof typeof dayMapping];
          if (dayField) {
            (newRow as any)[dayField] = '0';
          }
        }
      });

      state.createSchedule.employeeRows.push(newRow);
      state.createSchedule.nextEmployeeRowId += 1;
    },
    updateEmployeeRow(state, action: PayloadAction<{ id: string; updates: any; isSystemUpdate?: boolean }>) {
      const { id, updates, isSystemUpdate = false } = action.payload;
      const rowIndex = state.createSchedule.employeeRows.findIndex(row => row.id === id);
      if (rowIndex !== -1) {
        const oldRow = state.createSchedule.employeeRows[rowIndex];
        const hasPersistedFieldUpdate = [
          'employee',
          'role',
          'shift',
          'sunday',
          'monday',
          'tuesday',
          'wednesday',
          'thursday',
          'friday',
          'saturday',
        ].some((field) => Object.prototype.hasOwnProperty.call(updates, field));
        
        // Check if employee, role, or shift is being changed and the old row came from scheduleEmployeeData
        // Only add to deletedEmployeeRows if:
        // 1. User is making the change (not a system update)
        // 2. Either employee, role, or shift is being updated
        // 3. The old row has meaningful data (employee, role, shift are not null)
        // 4. This applies to both parent and child rows (split roles)
        if (!isSystemUpdate && 
            (updates.employee !== undefined || updates.role !== undefined || updates.shift !== undefined) &&
            oldRow.employee !== null && 
            oldRow.role !== null && 
            oldRow.shift !== null) {
          
          // Check if employee, role, or shift is actually changing
          const isEmployeeChanging = updates.employee !== undefined && 
            (updates.employee?.id !== oldRow.employee?.id || 
             updates.employee?.gpid !== oldRow.employee?.gpid || 
             updates.employee?.value !== oldRow.employee?.value);
          const isRoleChanging = updates.role !== undefined && 
            (updates.role?.id !== oldRow.role?.id || updates.role?.value !== oldRow.role?.value);
          const isShiftChanging = updates.shift !== undefined && 
            (updates.shift?.id !== oldRow.shift?.id || updates.shift?.value !== oldRow.shift?.value);
          
          if (isEmployeeChanging || isRoleChanging || isShiftChanging) {
            // Create a copy of the old row with ORIGINAL hours preserved
            // The transform function will set them to 0 when sending to API
            const deletedRow = {
              ...oldRow,
              // Keep original hours - don't set to 0 here
              // Mark this as a deleted row for tracking
              isDeleted: true,
              // Store original ID to avoid conflicts
              originalId: oldRow.id,
              // Generate a unique ID for the deleted row
              id: `deleted_${oldRow.id}_${Date.now()}`
            };
            
            // Add to deletedEmployeeRows if not already present
            const isDuplicate = state.createSchedule.deletedEmployeeRows.some(
              row => row.employee?.id === deletedRow.employee?.id && 
                     row.role?.id === deletedRow.role?.id && 
                     row.shift?.id === deletedRow.shift?.id
            );
            
            if (!isDuplicate) {
              state.createSchedule.deletedEmployeeRows.push(deletedRow);
              console.log('Added old row to deletedEmployeeRows (employee/role/shift changed):', deletedRow);
            }
          }
        }
        
        const newRow = {
          ...oldRow,
          ...updates,
        };
        
        state.createSchedule.employeeRows[rowIndex] = newRow;
        
        // Only set hasUnsavedChanges for user changes, not system updates (data loading)
        if (!isSystemUpdate && hasPersistedFieldUpdate) {
          // Check if we're adding meaningful data OR clearing previously meaningful data
          const newRowHasMeaningfulData = (
            newRow.employee !== null ||
            newRow.role !== null ||
            newRow.shift !== null ||
            (newRow.sunday && newRow.sunday !== '' && newRow.sunday !== '0') ||
            (newRow.monday && newRow.monday !== '' && newRow.monday !== '0') ||
            (newRow.tuesday && newRow.tuesday !== '' && newRow.tuesday !== '0') ||
            (newRow.wednesday && newRow.wednesday !== '' && newRow.wednesday !== '0') ||
            (newRow.thursday && newRow.thursday !== '' && newRow.thursday !== '0') ||
            (newRow.friday && newRow.friday !== '' && newRow.friday !== '0') ||
            (newRow.saturday && newRow.saturday !== '' && newRow.saturday !== '0')
          );
          
          const oldRowHadMeaningfulData = (
            oldRow.employee !== null ||
            oldRow.role !== null ||
            oldRow.shift !== null ||
            (oldRow.sunday && oldRow.sunday !== '' && oldRow.sunday !== '0') ||
            (oldRow.monday && oldRow.monday !== '' && oldRow.monday !== '0') ||
            (oldRow.tuesday && oldRow.tuesday !== '' && oldRow.tuesday !== '0') ||
            (oldRow.wednesday && oldRow.wednesday !== '' && oldRow.wednesday !== '0') ||
            (oldRow.thursday && oldRow.thursday !== '' && oldRow.thursday !== '0') ||
            (oldRow.friday && oldRow.friday !== '' && oldRow.friday !== '0') ||
            (oldRow.saturday && oldRow.saturday !== '' && oldRow.saturday !== '0')
          );
          
          // Set hasUnsavedChanges if we're adding meaningful data or if we had meaningful data before
          if (newRowHasMeaningfulData || oldRowHadMeaningfulData) {
            state.createSchedule.hasUnsavedChanges = true;
          }
        }
      }
    },
    removeEmployeeRow(state, action: PayloadAction<string>) {
      const id = action.payload;
      const rowToRemove = state.createSchedule.employeeRows.find(row => row.id === id);

      if (rowToRemove?.sourceSnapshot?.employee && rowToRemove?.sourceSnapshot?.role) {
        const snapshot = rowToRemove.sourceSnapshot;
        const hasBackendHours = [
          parseFloat(snapshot.sunday || '0') || 0,
          parseFloat(snapshot.monday || '0') || 0,
          parseFloat(snapshot.tuesday || '0') || 0,
          parseFloat(snapshot.wednesday || '0') || 0,
          parseFloat(snapshot.thursday || '0') || 0,
          parseFloat(snapshot.friday || '0') || 0,
          parseFloat(snapshot.saturday || '0') || 0,
        ].some(hours => hours > 0);

        if (hasBackendHours) {
          const isDuplicate = state.createSchedule.deletedEmployeeRows.some((row: any) =>
            row.employee?.id === snapshot.employee?.id &&
            row.role?.id === snapshot.role?.id &&
            row.shift?.value === snapshot.shift?.value
          );

          if (!isDuplicate) {
            state.createSchedule.deletedEmployeeRows.push({
              id: `deleted_${rowToRemove.id}_${Date.now()}`,
              isSelected: false,
              employee: snapshot.employee,
              role: snapshot.role,
              shift: snapshot.shift || null,
              sunday: snapshot.sunday,
              monday: snapshot.monday,
              tuesday: snapshot.tuesday,
              wednesday: snapshot.wednesday,
              thursday: snapshot.thursday,
              friday: snapshot.friday,
              saturday: snapshot.saturday,
              total: (
                (parseFloat(snapshot.sunday || '0') || 0) +
                (parseFloat(snapshot.monday || '0') || 0) +
                (parseFloat(snapshot.tuesday || '0') || 0) +
                (parseFloat(snapshot.wednesday || '0') || 0) +
                (parseFloat(snapshot.thursday || '0') || 0) +
                (parseFloat(snapshot.friday || '0') || 0) +
                (parseFloat(snapshot.saturday || '0') || 0)
              ).toString(),
              isDeleted: true,
              originalId: rowToRemove.id,
            });
          }
        }
      }
      
      // Check if the row being removed had meaningful data
      const hadMeaningfulData = rowToRemove && (
        rowToRemove.employee !== null ||
        rowToRemove.role !== null ||
        rowToRemove.shift !== null ||
        (rowToRemove.sunday && rowToRemove.sunday !== '' && rowToRemove.sunday !== '0') ||
        (rowToRemove.monday && rowToRemove.monday !== '' && rowToRemove.monday !== '0') ||
        (rowToRemove.tuesday && rowToRemove.tuesday !== '' && rowToRemove.tuesday !== '0') ||
        (rowToRemove.wednesday && rowToRemove.wednesday !== '' && rowToRemove.wednesday !== '0') ||
        (rowToRemove.thursday && rowToRemove.thursday !== '' && rowToRemove.thursday !== '0') ||
        (rowToRemove.friday && rowToRemove.friday !== '' && rowToRemove.friday !== '0') ||
        (rowToRemove.saturday && rowToRemove.saturday !== '' && rowToRemove.saturday !== '0')
      );
      
      state.createSchedule.employeeRows = state.createSchedule.employeeRows.filter(row => row.id !== id);
      
      if (hadMeaningfulData) {
        state.createSchedule.hasUnsavedChanges = true;
      }
    },
    // Role filtering actions
    setFilteredRoles(state, action: PayloadAction<RolesByType>) {
      state.createSchedule.filteredRoles = action.payload;
    },
    // Day filter actions
    toggleSelectedDay(state, action: PayloadAction<string>) {
      const day = action.payload;
      const selectedDays = state.createSchedule.selectedDays;
      if (selectedDays.includes(day)) {
        state.createSchedule.selectedDays = selectedDays.filter(d => d !== day);
      } else {
        state.createSchedule.selectedDays = [...selectedDays, day];
      }
    },
    setSelectedDays(state, action: PayloadAction<string[]>) {
      state.createSchedule.selectedDays = action.payload;
    },
    clearSelectedDays(state) {
      state.createSchedule.selectedDays = [];
    },
    setDayFilter(state, action: PayloadAction<DayItem[]>) {
      state.createSchedule.dayFilter = action.payload;
    },
    clearAllEmployeeRows(state) {
      state.createSchedule.employeeRows = [];
      state.createSchedule.deletedEmployeeRows = [];
      state.createSchedule.nextEmployeeRowId = 1;
    },
    clearScheduleEmployeeData(state) {
      state.createSchedule.scheduleEmployeeData = null;
      state.createSchedule.scheduleEmployeeDataError = null;
      state.createSchedule.initialSchedules = null;
      state.createSchedule.hasUnsavedChanges = false;
    },
    // Update schedule hours data action
    updateScheduleHours(state, action: PayloadAction<{ role: string; dayNumber: number; locationId: string; year: number; period: number; week: number; newScheduledHours: number }>) {
      const { role, dayNumber, locationId, year, period, week, newScheduledHours } = action.payload;
      
      // Find and update the scheduleHours data
      if (state.createSchedule.scheduleHours && state.createSchedule.scheduleHours.data && state.createSchedule.scheduleHours.data.data) {
        const scheduleHoursData = state.createSchedule.scheduleHours.data.data;
        
        // Find the matching record
        const recordIndex = scheduleHoursData.findIndex((item: any) => 
          item.role === role && 
          item.dayNumber === dayNumber &&
          item.locationId === locationId &&
          item.year === year &&
          item.period === period &&
          item.week === week
        );
        
        if (recordIndex !== -1) {
          // Update the existing record with cumulative scheduled hours
          scheduleHoursData[recordIndex].scheduledHours = newScheduledHours;
          
          // Recalculate variance if forecast hours exist
          if (scheduleHoursData[recordIndex].forecastHours !== null) {
            scheduleHoursData[recordIndex].variance = 
              parseFloat(scheduleHoursData[recordIndex].forecastHours) - newScheduledHours;
          }
        } else {
          // Create a new record if it doesn't exist
          const newRecord = {
            locationId: locationId,
            locationName: "", // Will be populated by the calling function
            year: year,
            period: period,
            week: week,
            dayNumber: dayNumber,
            role: role,
            forecastHours: null,
            scheduledHours: newScheduledHours,
            variance: null,
            roleType: "OTHER" // Default role type, could be improved by passing this parameter
          };
          
          scheduleHoursData.push(newRecord);
        }
      }
    },
    // Update actual hours data action (for Actual Hours tab)
    updateActualHours(state, action: PayloadAction<{ role: string; dayNumber: number; locationId: string; year: number; period: number; week: number; newActualHours: number }>) {
      const { role, dayNumber, locationId, year, period, week, newActualHours } = action.payload;
      
      // Find and update the actualHours data
      if (state.createActualHours.actualHours && state.createActualHours.actualHours.data && state.createActualHours.actualHours.data.data) {
        const actualHoursData = state.createActualHours.actualHours.data.data;
        
        // Find the matching record
        const recordIndex = actualHoursData.findIndex((item: any) => 
          item.role === role && 
          item.dayNumber === dayNumber &&
          item.locationId === locationId &&
          item.year === year &&
          item.period === period &&
          item.week === week
        );
        
        if (recordIndex !== -1) {
          // Update the existing record with cumulative actual hours
          actualHoursData[recordIndex].actualHours = newActualHours;
          
          // Recalculate variance if estimated hours exist
          if (actualHoursData[recordIndex].estimatedHours !== null) {
            actualHoursData[recordIndex].variance = 
              parseFloat(actualHoursData[recordIndex].estimatedHours) - newActualHours;
          }
        } else {
          // Create a new record if it doesn't exist
          const newRecord = {
            locationId: locationId,
            locationName: "",
            year: year,
            period: period,
            week: week,
            dayNumber: dayNumber,
            role: role,
            estimatedHours: null,
            actualHours: newActualHours,
            variance: null,
            roleType: "OTHER"
          };
          
          actualHoursData.push(newRecord);
        }
      }
    },
    splitEmployeeRole(state, action: PayloadAction<string>) {
      const parentRowId = action.payload;
      const parentRowIndex = state.createSchedule.employeeRows.findIndex(row => row.id === parentRowId);
      
      if (parentRowIndex !== -1) {
        const parentRow = state.createSchedule.employeeRows[parentRowIndex];
        
        // Create child row ID
        const childRowId = state.createSchedule.nextEmployeeRowId.toString();
        
        // Create the child row (split role row)
        const childRow = {
          id: childRowId,
          isSelected: false,
          employee: parentRow.employee, // Inherit employee from parent
          role: null, // Will be set by user in the split row
          shift: null, // Don't inherit shift from parent - let user select
          sunday: '',
          monday: '',
          tuesday: '',
          wednesday: '',
          thursday: '',
          friday: '',
          saturday: '',
          total: '',
          // Split role specific fields
          isSplit: false,
          parentId: parentRowId,
          childId: undefined,
          isChildRow: true,
          isLocked: false, // Shift is unlocked since no shift is set
        };
        
        // Update parent row to mark it as split and link to child
        state.createSchedule.employeeRows[parentRowIndex] = {
          ...parentRow,
          isSplit: true,
          childId: childRowId,
          isSelected: false, // Deselect parent when split
        };
        
        // Insert child row right after parent row
        state.createSchedule.employeeRows.splice(parentRowIndex + 1, 0, childRow);
        
        // Increment nextEmployeeRowId
        state.createSchedule.nextEmployeeRowId += 1;
        state.createSchedule.hasUnsavedChanges = true;
      }
    },
    removeSplitRole(state, action: PayloadAction<string>) {
      const childRowId = action.payload;
      const childRowIndex = state.createSchedule.employeeRows.findIndex(row => row.id === childRowId);
      
      if (childRowIndex !== -1) {
        const childRow = state.createSchedule.employeeRows[childRowIndex];
        const parentRowId = childRow.parentId;

        if (childRow.sourceSnapshot?.employee && childRow.sourceSnapshot?.role) {
          const snapshot = childRow.sourceSnapshot;
          const hasBackendHours = [
            parseFloat(snapshot.sunday || '0') || 0,
            parseFloat(snapshot.monday || '0') || 0,
            parseFloat(snapshot.tuesday || '0') || 0,
            parseFloat(snapshot.wednesday || '0') || 0,
            parseFloat(snapshot.thursday || '0') || 0,
            parseFloat(snapshot.friday || '0') || 0,
            parseFloat(snapshot.saturday || '0') || 0,
          ].some(hours => hours > 0);

          const isDuplicate = state.createSchedule.deletedEmployeeRows.some((row: any) =>
            row.employee?.id === snapshot.employee?.id &&
            row.role?.id === snapshot.role?.id &&
            row.shift?.value === snapshot.shift?.value
          );

          if (hasBackendHours && !isDuplicate) {
            state.createSchedule.deletedEmployeeRows.push({
              ...childRow,
              employee: snapshot.employee,
              role: snapshot.role,
              shift: snapshot.shift || null,
              sunday: snapshot.sunday,
              monday: snapshot.monday,
              tuesday: snapshot.tuesday,
              wednesday: snapshot.wednesday,
              thursday: snapshot.thursday,
              friday: snapshot.friday,
              saturday: snapshot.saturday,
            });
          }
        } else {
          // Store the deleted child row as-is without modifying hours.
          // The transformation to zero hours will be handled in transformEmployeeRowsToAPIFormat.
          state.createSchedule.deletedEmployeeRows.push({ ...childRow });
        }
        
        // Remove child row
        state.createSchedule.employeeRows.splice(childRowIndex, 1);
        
        if (parentRowId) {
          const parentRowIndex = state.createSchedule.employeeRows.findIndex(row => row.id === parentRowId);
          
          if (parentRowIndex !== -1) {
            // Check if there are any remaining child split roles for this parent
            const remainingChildRows = state.createSchedule.employeeRows.filter(row => 
              row.parentId === parentRowId
            );
            
            if (remainingChildRows.length === 0) {
              // No more child rows, restore parent to unsplit state but KEEP parent's own data
              state.createSchedule.employeeRows[parentRowIndex] = {
                ...state.createSchedule.employeeRows[parentRowIndex],
                isSplit: false,
                childId: undefined,
                isSelected: false,
                // Keep parent's existing week data and only recalculate total
              };
              
              // Recalculate parent's total based on its own hours only (no child hours to add)
              const parentRow = state.createSchedule.employeeRows[parentRowIndex];
              const parentHours = [
                parseFloat(parentRow.sunday) || 0,
                parseFloat(parentRow.monday) || 0,
                parseFloat(parentRow.tuesday) || 0,
                parseFloat(parentRow.wednesday) || 0,
                parseFloat(parentRow.thursday) || 0,
                parseFloat(parentRow.friday) || 0,
                parseFloat(parentRow.saturday) || 0,
              ].reduce((sum, hours) => sum + hours, 0);
              
              state.createSchedule.employeeRows[parentRowIndex].total = parentHours.toFixed(2);
            } else {
              // There are remaining child rows, recalculate parent's total
              // Parent total = parent's own hours + sum of all child hours
              const parentRow = state.createSchedule.employeeRows[parentRowIndex];
              
              // Calculate parent's own hours
              const parentHours = [
                parseFloat(parentRow.sunday) || 0,
                parseFloat(parentRow.monday) || 0,
                parseFloat(parentRow.tuesday) || 0,
                parseFloat(parentRow.wednesday) || 0,
                parseFloat(parentRow.thursday) || 0,
                parseFloat(parentRow.friday) || 0,
                parseFloat(parentRow.saturday) || 0,
              ].reduce((sum, hours) => sum + hours, 0);
              
              // Calculate sum of all remaining child hours
              const childHoursSum = remainingChildRows.reduce((sum, childRow) => {
                const childDayHours = [
                  parseFloat(childRow.sunday) || 0,
                  parseFloat(childRow.monday) || 0,
                  parseFloat(childRow.tuesday) || 0,
                  parseFloat(childRow.wednesday) || 0,
                  parseFloat(childRow.thursday) || 0,
                  parseFloat(childRow.friday) || 0,
                  parseFloat(childRow.saturday) || 0,
                ].reduce((daySum, hours) => daySum + hours, 0);
                return sum + childDayHours;
              }, 0);
              
              // Update parent's total
              state.createSchedule.employeeRows[parentRowIndex].total = (parentHours + childHoursSum).toFixed(2);
              
              // Ensure parent remains selected if it was before
              state.createSchedule.employeeRows[parentRowIndex].isSelected = false;
            }
          }
        }
        state.createSchedule.hasUnsavedChanges = true;
      }
    },

    splitRoleAfterSibling(state, action: PayloadAction<{ parentId: string; afterSiblingId: string }>) {
      const { parentId, afterSiblingId } = action.payload;
      const parentRowIndex = state.createSchedule.employeeRows.findIndex(row => row.id === parentId);
      
      if (parentRowIndex !== -1) {
        // Find the sibling row to insert after
        const siblingIndex = state.createSchedule.employeeRows.findIndex(row => row.id === afterSiblingId);
        
        if (siblingIndex !== -1) {
          const parentRow = state.createSchedule.employeeRows[parentRowIndex];
          
          // Create new child row with similar structure to existing children
          const newChildId = `split_${Date.now()}`;
          const childRow = {
            id: newChildId,
            isSelected: true, // Set to true so it gets included in payload
            employee: parentRow.employee, // Inherit employee from parent
            role: null, // Will be set by user in the split row
            shift: null, // Don't inherit shift from parent - let user select
            sunday: '',
            monday: '',
            tuesday: '',
            wednesday: '',
            thursday: '',
            friday: '',
            saturday: '',
            total: '0',
            isSplit: false,
            parentId: parentId,
            isChildRow: true,
            isLocked: false, // Shift is unlocked since no shift is set
          };
          
          // Insert the new child row right after the specified sibling
          state.createSchedule.employeeRows.splice(siblingIndex + 1, 0, childRow);
          
          // Ensure parent is marked as split
          state.createSchedule.employeeRows[parentRowIndex].isSplit = true;
        }
      }
      state.createSchedule.hasUnsavedChanges = true;
    },

    // Actual Hours actions
    setCreateActualHoursLoading(state, action: PayloadAction<boolean>) {
      state.createActualHours.loading = action.payload;
    },
    setCreateActualHoursError(state, action: PayloadAction<string | null>) {
      state.createActualHours.error = action.payload;
    },
    setCreateActualHoursData(state, action: PayloadAction<any>) {
      state.createActualHours.actualHoursData = action.payload;
    },
    // Period, Week, and Year actions for Actual Hours functionality
    setCreateActualHoursPeriodWeek(state, action: PayloadAction<{ period: number; week: number; year: number }>) {
      state.createActualHours.period = action.payload.period;
      state.createActualHours.week = action.payload.week;
      state.createActualHours.year = action.payload.year;
    },
    clearCreateActualHoursPeriodWeek(state) {
      state.createActualHours.period = null;
      state.createActualHours.week = null;
      state.createActualHours.year = null;
    },
    // Actual Employee rows management actions
    addActualEmployeeRow(state) {
      // Check actualDayFilter to determine which days should be set to "0"
      const dayMapping = {
        '0': 'sunday',
        '1': 'monday', 
        '2': 'tuesday',
        '3': 'wednesday',
        '4': 'thursday',
        '5': 'friday',
        '6': 'saturday'
      };

      const newRow = {
        id: state.createActualHours.nextActualEmployeeRowId.toString(),
        isSelected: false,
        employee: null,
        role: null,
        shift: null,
        sunday: '',
        monday: '',
        tuesday: '',
        wednesday: '',
        thursday: '',
        friday: '',
        saturday: '',
        total: '',
        scheduleVariance: '0.00',
      };

      // Set disabled days to "0"
      state.createActualHours.actualDayFilter.forEach(day => {
        if (!day.enabled) {
          const dayField = dayMapping[day.id as keyof typeof dayMapping];
          if (dayField) {
            (newRow as any)[dayField] = '0';
          }
        }
      });

      state.createActualHours.actualEmployeeRows.push(newRow);
      state.createActualHours.nextActualEmployeeRowId++;
    },
    updateActualEmployeeRow(state, action: PayloadAction<{ id: string; updates: any; isSystemUpdate?: boolean }>) {
      const { id, updates, isSystemUpdate = false } = action.payload;
      const rowIndex = state.createActualHours.actualEmployeeRows.findIndex(row => row.id === id);
      if (rowIndex !== -1) {
        const oldRow = state.createActualHours.actualEmployeeRows[rowIndex];
        const hasPersistedFieldUpdate = [
          'employee',
          'role',
          'shift',
          'sunday',
          'monday',
          'tuesday',
          'wednesday',
          'thursday',
          'friday',
          'saturday',
        ].some((field) => Object.prototype.hasOwnProperty.call(updates, field));
        
        // Check if employee, role, or shift is being changed and the old row came from scheduleEmployeeData
        // Only add to actualDeletedEmployeeRows if:
        // 1. User is making the change (not a system update)
        // 2. Either employee, role, or shift is being updated
        // 3. The old row has meaningful data (employee, role, shift are not null)
        // 4. This applies to both parent and child rows (split roles)
        if (!isSystemUpdate && 
            (updates.employee !== undefined || updates.role !== undefined || updates.shift !== undefined) &&
            oldRow.employee !== null && 
            oldRow.role !== null && 
            oldRow.shift !== null) {
          
          // Check if employee, role, or shift is actually changing
          const isEmployeeChanging = updates.employee !== undefined && 
            (updates.employee?.id !== oldRow.employee?.id || 
             updates.employee?.gpid !== oldRow.employee?.gpid || 
             updates.employee?.value !== oldRow.employee?.value);
          const isRoleChanging = updates.role !== undefined && 
            (updates.role?.id !== oldRow.role?.id || updates.role?.value !== oldRow.role?.value);
          const isShiftChanging = updates.shift !== undefined && 
            (updates.shift?.id !== oldRow.shift?.id || updates.shift?.value !== oldRow.shift?.value);
          
          if (isEmployeeChanging || isRoleChanging || isShiftChanging) {
            // Create a copy of the old row with ORIGINAL hours preserved
            // The transform function will set them to 0 when sending to API
            const deletedRow = {
              ...oldRow,
              // Keep original hours - don't set to 0 here
              // Mark this as a deleted row for tracking
              isDeleted: true,
              // Store original ID to avoid conflicts
              originalId: oldRow.id,
              // Generate a unique ID for the deleted row
              id: `deleted_actual_${oldRow.id}_${Date.now()}`
            };
            
            // Add to actualDeletedEmployeeRows if not already present
            const isDuplicate = state.createActualHours.actualDeletedEmployeeRows.some(
              row => row.employee?.id === deletedRow.employee?.id && 
                     row.role?.id === deletedRow.role?.id && 
                     row.shift?.id === deletedRow.shift?.id
            );
            
            if (!isDuplicate) {
              state.createActualHours.actualDeletedEmployeeRows.push(deletedRow);
              console.log('Added old actual row to actualDeletedEmployeeRows (employee/role/shift changed):', deletedRow);
            }
          }
        }
        
        const newRow = {
          ...oldRow,
          ...updates,
        };
        
        state.createActualHours.actualEmployeeRows[rowIndex] = newRow;
        
        // Only set hasUnsavedChanges for user changes, not system updates (data loading)
        if (!isSystemUpdate && hasPersistedFieldUpdate) {
          // Check if we're adding meaningful data OR clearing previously meaningful data
          const newRowHasMeaningfulData = (
            newRow.employee !== null ||
            newRow.role !== null ||
            newRow.shift !== null ||
            (newRow.sunday && newRow.sunday !== '' && newRow.sunday !== '0') ||
            (newRow.monday && newRow.monday !== '' && newRow.monday !== '0') ||
            (newRow.tuesday && newRow.tuesday !== '' && newRow.tuesday !== '0') ||
            (newRow.wednesday && newRow.wednesday !== '' && newRow.wednesday !== '0') ||
            (newRow.thursday && newRow.thursday !== '' && newRow.thursday !== '0') ||
            (newRow.friday && newRow.friday !== '' && newRow.friday !== '0') ||
            (newRow.saturday && newRow.saturday !== '' && newRow.saturday !== '0')
          );
          
          const oldRowHadMeaningfulData = (
            oldRow.employee !== null ||
            oldRow.role !== null ||
            oldRow.shift !== null ||
            (oldRow.sunday && oldRow.sunday !== '' && oldRow.sunday !== '0') ||
            (oldRow.monday && oldRow.monday !== '' && oldRow.monday !== '0') ||
            (oldRow.tuesday && oldRow.tuesday !== '' && oldRow.tuesday !== '0') ||
            (oldRow.wednesday && oldRow.wednesday !== '' && oldRow.wednesday !== '0') ||
            (oldRow.thursday && oldRow.thursday !== '' && oldRow.thursday !== '0') ||
            (oldRow.friday && oldRow.friday !== '' && oldRow.friday !== '0') ||
            (oldRow.saturday && oldRow.saturday !== '' && oldRow.saturday !== '0')
          );
          
          // Set hasUnsavedChanges if we're adding meaningful data or if we had meaningful data before
          if (newRowHasMeaningfulData || oldRowHadMeaningfulData) {
            state.createSchedule.hasUnsavedChanges = true;
          }
        }
      }
    },
    removeActualEmployeeRow(state, action: PayloadAction<string>) {
      const id = action.payload;
      const rowToRemove = state.createActualHours.actualEmployeeRows.find(row => row.id === id);

      if (rowToRemove?.sourceSnapshot?.employee && rowToRemove?.sourceSnapshot?.role) {
        const snapshot = rowToRemove.sourceSnapshot;
        const hasBackendHours = [
          parseFloat(snapshot.sunday || '0') || 0,
          parseFloat(snapshot.monday || '0') || 0,
          parseFloat(snapshot.tuesday || '0') || 0,
          parseFloat(snapshot.wednesday || '0') || 0,
          parseFloat(snapshot.thursday || '0') || 0,
          parseFloat(snapshot.friday || '0') || 0,
          parseFloat(snapshot.saturday || '0') || 0,
        ].some(hours => hours > 0);

        if (hasBackendHours) {
          const isDuplicate = state.createActualHours.actualDeletedEmployeeRows.some((row: any) =>
            row.employee?.id === snapshot.employee?.id &&
            row.role?.id === snapshot.role?.id &&
            row.shift?.value === snapshot.shift?.value
          );

          if (!isDuplicate) {
            state.createActualHours.actualDeletedEmployeeRows.push({
              id: `deleted_actual_${rowToRemove.id}_${Date.now()}`,
              isSelected: false,
              employee: snapshot.employee,
              role: snapshot.role,
              shift: snapshot.shift || null,
              sunday: snapshot.sunday,
              monday: snapshot.monday,
              tuesday: snapshot.tuesday,
              wednesday: snapshot.wednesday,
              thursday: snapshot.thursday,
              friday: snapshot.friday,
              saturday: snapshot.saturday,
              total: (
                (parseFloat(snapshot.sunday || '0') || 0) +
                (parseFloat(snapshot.monday || '0') || 0) +
                (parseFloat(snapshot.tuesday || '0') || 0) +
                (parseFloat(snapshot.wednesday || '0') || 0) +
                (parseFloat(snapshot.thursday || '0') || 0) +
                (parseFloat(snapshot.friday || '0') || 0) +
                (parseFloat(snapshot.saturday || '0') || 0)
              ).toString(),
              isDeleted: true,
              originalId: rowToRemove.id,
            });
          }
        }
      }
      
      // Check if the row being removed had meaningful data
      const hadMeaningfulData = rowToRemove && (
        rowToRemove.employee !== null ||
        rowToRemove.role !== null ||
        rowToRemove.shift !== null ||
        (rowToRemove.sunday && rowToRemove.sunday !== '' && rowToRemove.sunday !== '0') ||
        (rowToRemove.monday && rowToRemove.monday !== '' && rowToRemove.monday !== '0') ||
        (rowToRemove.tuesday && rowToRemove.tuesday !== '' && rowToRemove.tuesday !== '0') ||
        (rowToRemove.wednesday && rowToRemove.wednesday !== '' && rowToRemove.wednesday !== '0') ||
        (rowToRemove.thursday && rowToRemove.thursday !== '' && rowToRemove.thursday !== '0') ||
        (rowToRemove.friday && rowToRemove.friday !== '' && rowToRemove.friday !== '0') ||
        (rowToRemove.saturday && rowToRemove.saturday !== '' && rowToRemove.saturday !== '0')
      );
      
      state.createActualHours.actualEmployeeRows = state.createActualHours.actualEmployeeRows.filter(row => row.id !== id);
      
      if (hadMeaningfulData) {
        state.createSchedule.hasUnsavedChanges = true;
      }
    },
    // Actual Hours role filtering actions
    setActualFilteredRoles(state, action: PayloadAction<RolesByType>) {
      state.createActualHours.actualFilteredRoles = action.payload;
    },
    // Actual Hours day filter actions
    toggleActualSelectedDay(state, action: PayloadAction<string>) {
      const day = action.payload;
      const selectedDays = state.createActualHours.actualSelectedDays;
      if (selectedDays.includes(day)) {
        state.createActualHours.actualSelectedDays = selectedDays.filter(d => d !== day);
      } else {
        state.createActualHours.actualSelectedDays = [...selectedDays, day];
      }
    },
    setActualSelectedDays(state, action: PayloadAction<string[]>) {
      state.createActualHours.actualSelectedDays = action.payload;
    },
    clearActualSelectedDays(state) {
      state.createActualHours.actualSelectedDays = [];
    },
    setActualDayFilter(state, action: PayloadAction<DayItem[]>) {
      state.createActualHours.actualDayFilter = action.payload;
    },
    clearAllActualEmployeeRows(state) {
      state.createActualHours.actualEmployeeRows = [];
      state.createActualHours.actualDeletedEmployeeRows = [];
      state.createActualHours.nextActualEmployeeRowId = 1;
    },

    // Actual Hours Split Role actions
    splitActualEmployeeRole(state, action: PayloadAction<string>) {
      const parentRowId = action.payload;
      const parentRowIndex = state.createActualHours.actualEmployeeRows.findIndex(row => row.id === parentRowId);
      
      if (parentRowIndex !== -1) {
        const parentRow = state.createActualHours.actualEmployeeRows[parentRowIndex];
        const childRowId = `actual_split_${state.createActualHours.nextActualEmployeeRowId}`;
        
        // Create new child row with empty data
        const childRow = {
          id: childRowId,
          isSelected: false,
          employee: parentRow.employee, // Copy employee from parent
          role: null, // Role will be selected separately
          shift: null, // Don't copy shift from parent - let user select
          sunday: '',
          monday: '',
          tuesday: '',
          wednesday: '',
          thursday: '',
          friday: '',
          saturday: '',
          total: '',
          scheduleVariance: '0.00',
          // Split role specific fields
          isSplit: false,
          parentId: parentRowId,
          childId: undefined,
          isChildRow: true,
          isLocked: false, // Shift is unlocked since no shift is set
        };
        
        // Update parent row to mark it as split and link to child
        state.createActualHours.actualEmployeeRows[parentRowIndex] = {
          ...parentRow,
          isSplit: true,
          childId: childRowId,
          isSelected: false, // Deselect parent when split
        };
        
        // Insert child row right after parent row
        state.createActualHours.actualEmployeeRows.splice(parentRowIndex + 1, 0, childRow);
        
        // Increment nextActualEmployeeRowId
        state.createActualHours.nextActualEmployeeRowId += 1;
        state.createSchedule.hasUnsavedChanges = true;
      }
    },
    removeActualSplitRole(state, action: PayloadAction<string>) {
      const childRowId = action.payload;
      const childRowIndex = state.createActualHours.actualEmployeeRows.findIndex(row => row.id === childRowId);
      
      if (childRowIndex !== -1) {
        const childRow = state.createActualHours.actualEmployeeRows[childRowIndex];
        const parentRowId = childRow.parentId;

        if (childRow.sourceSnapshot?.employee && childRow.sourceSnapshot?.role) {
          const snapshot = childRow.sourceSnapshot;
          const hasBackendHours = [
            parseFloat(snapshot.sunday || '0') || 0,
            parseFloat(snapshot.monday || '0') || 0,
            parseFloat(snapshot.tuesday || '0') || 0,
            parseFloat(snapshot.wednesday || '0') || 0,
            parseFloat(snapshot.thursday || '0') || 0,
            parseFloat(snapshot.friday || '0') || 0,
            parseFloat(snapshot.saturday || '0') || 0,
          ].some(hours => hours > 0);

          const isDuplicate = state.createActualHours.actualDeletedEmployeeRows.some((row: any) =>
            row.employee?.id === snapshot.employee?.id &&
            row.role?.id === snapshot.role?.id &&
            row.shift?.value === snapshot.shift?.value
          );

          if (hasBackendHours && !isDuplicate) {
            state.createActualHours.actualDeletedEmployeeRows.push({
              ...childRow,
              employee: snapshot.employee,
              role: snapshot.role,
              shift: snapshot.shift || null,
              sunday: snapshot.sunday,
              monday: snapshot.monday,
              tuesday: snapshot.tuesday,
              wednesday: snapshot.wednesday,
              thursday: snapshot.thursday,
              friday: snapshot.friday,
              saturday: snapshot.saturday,
            });
          }
        } else {
          state.createActualHours.actualDeletedEmployeeRows.push({ ...childRow });
        }
        
        // Remove child row
        state.createActualHours.actualEmployeeRows.splice(childRowIndex, 1);
        
        if (parentRowId) {
          const parentRowIndex = state.createActualHours.actualEmployeeRows.findIndex(row => row.id === parentRowId);
          
          if (parentRowIndex !== -1) {
            // Check if there are any remaining child split roles for this parent
            const remainingChildRows = state.createActualHours.actualEmployeeRows.filter(row => 
              row.parentId === parentRowId
            );
            
            if (remainingChildRows.length === 0) {
              // No more child rows, restore parent to unsplit state but KEEP parent's own data
              state.createActualHours.actualEmployeeRows[parentRowIndex] = {
                ...state.createActualHours.actualEmployeeRows[parentRowIndex],
                isSplit: false,
                childId: undefined,
                isSelected: false,
                // Keep parent's existing week data and only recalculate total
              };
              
              // Recalculate parent's total based on its own hours only (no child hours to add)
              const parentRow = state.createActualHours.actualEmployeeRows[parentRowIndex];
              const parentHours = [
                parseFloat(parentRow.sunday) || 0,
                parseFloat(parentRow.monday) || 0,
                parseFloat(parentRow.tuesday) || 0,
                parseFloat(parentRow.wednesday) || 0,
                parseFloat(parentRow.thursday) || 0,
                parseFloat(parentRow.friday) || 0,
                parseFloat(parentRow.saturday) || 0,
              ].reduce((sum, hours) => sum + hours, 0);
              
              state.createActualHours.actualEmployeeRows[parentRowIndex].total = parentHours.toFixed(2);
            } else {
              // There are remaining child rows, recalculate parent's total
              // Parent total = parent's own hours + sum of all child hours
              const parentRow = state.createActualHours.actualEmployeeRows[parentRowIndex];
              
              // Calculate parent's own hours
              const parentHours = [
                parseFloat(parentRow.sunday) || 0,
                parseFloat(parentRow.monday) || 0,
                parseFloat(parentRow.tuesday) || 0,
                parseFloat(parentRow.wednesday) || 0,
                parseFloat(parentRow.thursday) || 0,
                parseFloat(parentRow.friday) || 0,
                parseFloat(parentRow.saturday) || 0,
              ].reduce((sum, hours) => sum + hours, 0);
              
              // Calculate sum of all remaining child hours
              const childHoursSum = remainingChildRows.reduce((sum, childRow) => {
                const childDayHours = [
                  parseFloat(childRow.sunday) || 0,
                  parseFloat(childRow.monday) || 0,
                  parseFloat(childRow.tuesday) || 0,
                  parseFloat(childRow.wednesday) || 0,
                  parseFloat(childRow.thursday) || 0,
                  parseFloat(childRow.friday) || 0,
                  parseFloat(childRow.saturday) || 0,
                ].reduce((daySum, hours) => daySum + hours, 0);
                return sum + childDayHours;
              }, 0);
              
              // Update parent's total
              state.createActualHours.actualEmployeeRows[parentRowIndex].total = (parentHours + childHoursSum).toFixed(2);
              
              // Ensure parent remains selected if it was before
              state.createActualHours.actualEmployeeRows[parentRowIndex].isSelected = false;
            }
          }
        }
      }
    },

    splitActualRoleAfterSibling(state, action: PayloadAction<{ parentId: string; afterSiblingId: string }>) {
      const { parentId, afterSiblingId } = action.payload;
      const parentRowIndex = state.createActualHours.actualEmployeeRows.findIndex(row => row.id === parentId);
      
      if (parentRowIndex !== -1) {
        // Find the sibling row to insert after
        const siblingIndex = state.createActualHours.actualEmployeeRows.findIndex(row => row.id === afterSiblingId);
        
        if (siblingIndex !== -1) {
          const parentRow = state.createActualHours.actualEmployeeRows[parentRowIndex];
          
          // Create new child row with similar structure to existing children
          const newChildId = `actual_split_${Date.now()}`;
          const childRow = {
            id: newChildId,
            isSelected: true, // Set to true so it gets included in payload
            employee: parentRow.employee, // Inherit employee from parent
            role: null, // Will be set by user in the split row
            shift: null, // Don't inherit shift from parent - let user select
            sunday: '',
            monday: '',
            tuesday: '',
            wednesday: '',
            thursday: '',
            friday: '',
            saturday: '',
            total: '0',
            scheduleVariance: '0.00',
            isSplit: false,
            parentId: parentId,
            isChildRow: true,
            isLocked: false, // Shift is unlocked since no shift is set
          };
          
          // Insert the new child row right after the specified sibling
          state.createActualHours.actualEmployeeRows.splice(siblingIndex + 1, 0, childRow);
          
          // Ensure parent is marked as split
          state.createActualHours.actualEmployeeRows[parentRowIndex].isSplit = true;
        }
      }
    },

    // Copy Schedule Hours data to Actual Hours
    copyScheduleToActualHours(state) {
      // Clear existing actual hours data
      state.createActualHours.actualEmployeeRows = [];
      
      // Deep copy all employee rows from schedule to actual hours
      const copiedRows = state.createSchedule.employeeRows.map((row: any) => {
        // Create deep copy of the row using JSON parse/stringify for complete deep copy
        const deepCopiedRow = JSON.parse(JSON.stringify(row));
        
        // Update properties after deep copy
        deepCopiedRow.id = `actual_${row.id}`;
        deepCopiedRow.isSelected = false; // Reset selection state
        
        // Update parent/child relationships with new IDs
        if (deepCopiedRow.parentId) {
          deepCopiedRow.parentId = `actual_${row.parentId}`;
        }
        if (deepCopiedRow.childId) {
          deepCopiedRow.childId = `actual_${row.childId}`;
        }
        
        return deepCopiedRow;
      });
      
      // Set the copied rows to actual employee rows
      state.createActualHours.actualEmployeeRows = copiedRows;
      
      // Update the next ID counter to avoid conflicts
      const maxIdNumber = copiedRows.reduce((max: number, row: any) => {
        const idNumber = parseInt(row.id.replace('actual_', ''), 10);
        return isNaN(idNumber) ? max : Math.max(max, idNumber);
      }, state.createActualHours.nextActualEmployeeRowId);
      
      state.createActualHours.nextActualEmployeeRowId = maxIdNumber + 1;
      
      // Copy role filtering and day filtering states
      state.createActualHours.actualFilteredRoles = JSON.parse(JSON.stringify(state.createSchedule.filteredRoles));
      state.createActualHours.actualDayFilter = JSON.parse(JSON.stringify(state.createSchedule.dayFilter));
      state.createActualHours.actualSelectedDays = [...state.createSchedule.selectedDays];
    },
  },
  extraReducers: (builder) => {
    builder
      // KCPH Data
      .addCase(fetchKCPHData.pending, (state) => {
        state.dashboard.kcphLoading = true;
        state.dashboard.kcphError = null;
      })
      .addCase(fetchKCPHData.fulfilled, (state, action) => {
        state.dashboard.kcphLoading = false;
        state.dashboard.kcphData = action.payload;
      })
      .addCase(fetchKCPHData.rejected, (state, action) => {
        state.dashboard.kcphLoading = false;
        state.dashboard.kcphError = action.payload as string || 'Failed to fetch KCPH data';
      })
      // Dashboard Hours Data
      .addCase(fetchDashboardHours.pending, (state) => {
        state.dashboard.hoursLoading = true;
        state.dashboard.hoursError = null;
      })
      .addCase(fetchDashboardHours.fulfilled, (state, action) => {
        state.dashboard.hoursLoading = false;
        state.dashboard.hoursData = action.payload;
      })
      .addCase(fetchDashboardHours.rejected, (state, action) => {
        state.dashboard.hoursLoading = false;
        state.dashboard.hoursError = action.payload as string || 'Failed to fetch dashboard hours data';
      })
      // Dashboard Schedule Data
      .addCase(fetchDashboardSchedule.pending, (state) => {
        state.dashboard.scheduleLoading = true;
        state.dashboard.scheduleError = null;
        // Only set filtered loading if this is a filtered call
        state.dashboard.filteredScheduleLoading = false;
        state.dashboard.filteredScheduleError = null;
      })
      .addCase(fetchDashboardSchedule.fulfilled, (state, action) => {
        state.dashboard.scheduleLoading = false;
        // If the action.meta.arg is empty or only has locationId, treat as base data
        const filters = action.meta?.arg || {};
        const filterKeys = Object.keys(filters).filter(k => k !== 'locationId');
        if (filterKeys.length === 0) {
          state.dashboard.scheduleData = action.payload;
          state.dashboard.filteredScheduleData = null;
        } else {
          state.dashboard.filteredScheduleData = action.payload;
        }
      })
      .addCase(fetchDashboardSchedule.rejected, (state, action) => {
        state.dashboard.scheduleLoading = false;
        // If the action.meta.arg is empty or only has locationId, treat as base error
        const filters = action.meta?.arg || {};
        const filterKeys = Object.keys(filters).filter(k => k !== 'locationId');
        if (filterKeys.length === 0) {
          state.dashboard.scheduleError = action.payload as string || 'Failed to fetch dashboard schedule data';
          state.dashboard.filteredScheduleError = null;
        } else {
          state.dashboard.filteredScheduleError = action.payload as string || 'Failed to fetch filtered dashboard schedule data';
        }
      })
      // Copy Schedule
      .addCase(copySchedule.pending, (state) => {
        state.createSchedule.copyScheduleLoading = true;
        state.createSchedule.copyScheduleError = null;
      })
      .addCase(copySchedule.fulfilled, (state, action) => {
        state.createSchedule.copyScheduleLoading = false;
        state.createSchedule.copyScheduleError = null;
      })
      .addCase(copySchedule.rejected, (state, action) => {
        state.createSchedule.copyScheduleLoading = false;
        state.createSchedule.copyScheduleError = action.payload as string || 'Failed to copy schedule';
      })
      // DSD Zone Volume Distribution
      .addCase(fetchDSDZoneVolumeDistributionByDay.pending, (state) => {
        state.locationSetting.dsdLoading = true;
        state.locationSetting.loading = true; // Set overall loading to true
        state.locationSetting.error = null;
      })
      .addCase(fetchDSDZoneVolumeDistributionByDay.fulfilled, (state, action) => {
        state.locationSetting.dsdLoading = false;
        state.locationSetting.DSDVolumeDistributionByDay = action.payload;
        // Only set loading to false if all API calls are complete
        state.locationSetting.loading = state.locationSetting.nonDsdLoading || state.locationSetting.dsdDailyLoading || state.locationSetting.nonDsdDailyLoading || state.locationSetting.shiftTimesLoading || state.locationSetting.historicalHoursLoading;
      })
      .addCase(fetchDSDZoneVolumeDistributionByDay.rejected, (state, action) => {
        state.locationSetting.dsdLoading = false;
        state.locationSetting.error = action.payload as string || 'Failed to fetch DSD Zone Volume Distribution';
        // Only set loading to false if all API calls are complete
        state.locationSetting.loading = state.locationSetting.nonDsdLoading || state.locationSetting.dsdDailyLoading || state.locationSetting.nonDsdDailyLoading || state.locationSetting.shiftTimesLoading || state.locationSetting.historicalHoursLoading;
      })
      // NON-DSD Zone Volume Distribution
      .addCase(fetchNONDSDZoneVolumeDistributionByDay.pending, (state) => {
        state.locationSetting.nonDsdLoading = true;
        state.locationSetting.loading = true; // Set overall loading to true
        state.locationSetting.error = null;
      })
      .addCase(fetchNONDSDZoneVolumeDistributionByDay.fulfilled, (state, action) => {
        state.locationSetting.nonDsdLoading = false;
        state.locationSetting.NONDSDVolumeDistributionByDay = Array.isArray(action.payload)
          ? { data: action.payload }
          : action.payload;
        // Only set loading to false if all API calls are complete
        state.locationSetting.loading = state.locationSetting.dsdLoading || state.locationSetting.dsdDailyLoading || state.locationSetting.nonDsdDailyLoading || state.locationSetting.shiftTimesLoading || state.locationSetting.historicalHoursLoading;
      })
      .addCase(fetchNONDSDZoneVolumeDistributionByDay.rejected, (state, action) => {
        state.locationSetting.nonDsdLoading = false;
        state.locationSetting.error = action.payload as string || 'Failed to fetch NON-DSD Zone Volume Distribution';
        // Only set loading to false if all API calls are complete
        state.locationSetting.loading = state.locationSetting.dsdLoading || state.locationSetting.dsdDailyLoading || state.locationSetting.nonDsdDailyLoading || state.locationSetting.shiftTimesLoading || state.locationSetting.historicalHoursLoading;
      })
      // DSD Daily Percentage
      .addCase(fetchDSDDailyPercentage.pending, (state) => {
        state.locationSetting.dsdDailyLoading = true;
        state.locationSetting.loading = true;
        state.locationSetting.error = null;
      })
      .addCase(fetchDSDDailyPercentage.fulfilled, (state, action) => {
        state.locationSetting.dsdDailyLoading = false;
        state.locationSetting.DSDDailyPercentage = action.payload;
        // Only set loading to false if all API calls are complete
        state.locationSetting.loading = state.locationSetting.dsdLoading || state.locationSetting.nonDsdLoading || state.locationSetting.nonDsdDailyLoading || state.locationSetting.shiftTimesLoading || state.locationSetting.historicalHoursLoading;
      })
      .addCase(fetchDSDDailyPercentage.rejected, (state, action) => {
        state.locationSetting.dsdDailyLoading = false;
        state.locationSetting.error = action.payload as string || 'Failed to fetch DSD Daily Percentage';
        // Only set loading to false if all API calls are complete
        state.locationSetting.loading = state.locationSetting.dsdLoading || state.locationSetting.nonDsdLoading || state.locationSetting.nonDsdDailyLoading || state.locationSetting.shiftTimesLoading || state.locationSetting.historicalHoursLoading;
      })
      // NON-DSD Daily Percentage
      .addCase(fetchNONDSDDailyPercentage.pending, (state) => {
        state.locationSetting.nonDsdDailyLoading = true;
        state.locationSetting.loading = true;
        state.locationSetting.error = null;
      })
      .addCase(fetchNONDSDDailyPercentage.fulfilled, (state, action) => {
        state.locationSetting.nonDsdDailyLoading = false;
        state.locationSetting.NONDSDDailyPercentage = action.payload;
        // Only set loading to false if all API calls are complete
        state.locationSetting.loading = state.locationSetting.dsdLoading || state.locationSetting.nonDsdLoading || state.locationSetting.dsdDailyLoading || state.locationSetting.shiftTimesLoading || state.locationSetting.historicalHoursLoading;
      })
      .addCase(fetchNONDSDDailyPercentage.rejected, (state, action) => {
        state.locationSetting.nonDsdDailyLoading = false;
        state.locationSetting.error = action.payload as string || 'Failed to fetch NON-DSD Daily Percentage';
        // Only set loading to false if all API calls are complete
        state.locationSetting.loading = state.locationSetting.dsdLoading || state.locationSetting.nonDsdLoading || state.locationSetting.dsdDailyLoading || state.locationSetting.shiftTimesLoading || state.locationSetting.historicalHoursLoading;
      })
      // Shift Times
      .addCase(fetchShiftTimes.pending, (state) => {
        state.locationSetting.shiftTimesLoading = true;
        state.locationSetting.loading = true;
        state.locationSetting.error = null;
      })
      .addCase(fetchShiftTimes.fulfilled, (state, action) => {
        state.locationSetting.shiftTimesLoading = false;
        state.locationSetting.shiftTimes = action.payload;
        // Only set loading to false if all API calls are complete
        state.locationSetting.loading = state.locationSetting.dsdLoading || state.locationSetting.nonDsdLoading || state.locationSetting.dsdDailyLoading || state.locationSetting.nonDsdDailyLoading || state.locationSetting.historicalHoursLoading;
      })
      .addCase(fetchShiftTimes.rejected, (state, action) => {
        state.locationSetting.shiftTimesLoading = false;
        state.locationSetting.error = action.payload as string || 'Failed to fetch Shift Times';
        // Only set loading to false if all API calls are complete
        state.locationSetting.loading = state.locationSetting.dsdLoading || state.locationSetting.nonDsdLoading || state.locationSetting.dsdDailyLoading || state.locationSetting.nonDsdDailyLoading || state.locationSetting.historicalHoursLoading;
      })
      // Historical Hours
      .addCase(fetchHistoricalHours.pending, (state) => {
        state.locationSetting.historicalHoursLoading = true;
        state.locationSetting.loading = true;
        state.locationSetting.error = null;
      })
      .addCase(fetchHistoricalHours.fulfilled, (state, action) => {
        state.locationSetting.historicalHoursLoading = false;
        state.locationSetting.historicalHours = action.payload;
        // Only set loading to false if all API calls are complete
        state.locationSetting.loading = state.locationSetting.dsdLoading || state.locationSetting.nonDsdLoading || state.locationSetting.dsdDailyLoading || state.locationSetting.nonDsdDailyLoading || state.locationSetting.shiftTimesLoading;
      })
      .addCase(fetchHistoricalHours.rejected, (state, action) => {
        state.locationSetting.historicalHoursLoading = false;
        state.locationSetting.error = action.payload as string || 'Failed to fetch Historical Hours';
        // Only set loading to false if all API calls are complete
        state.locationSetting.loading = state.locationSetting.dsdLoading || state.locationSetting.nonDsdLoading || state.locationSetting.dsdDailyLoading || state.locationSetting.nonDsdDailyLoading || state.locationSetting.shiftTimesLoading;
      })
      // Schedule Settings Forecast Daily
      .addCase(fetchScheduleSettingsForecastDaily.pending, (state) => {
        state.scheduleSetting.forecastDailyLoading = true;
        state.scheduleSetting.loading = true;
        state.scheduleSetting.error = null;
      })
      .addCase(fetchScheduleSettingsForecastDaily.fulfilled, (state, action) => {
        state.scheduleSetting.forecastDailyLoading = false;
        state.scheduleSetting.loading = false;
        if (action.payload !== null) {
          state.scheduleSetting.scheduleSettingsForecastDaily = action.payload;
        }
        // If payload is null, it means there was an error handled centrally
      })
      .addCase(fetchScheduleSettingsForecastDaily.rejected, (state, action) => {
        state.scheduleSetting.forecastDailyLoading = false;
        state.scheduleSetting.error = action.payload as string || 'Failed to fetch Schedule Settings Forecast Daily';
        state.scheduleSetting.loading = false;
      })
      // Schedule Settings Forecast Daily NON-DSD
      .addCase(fetchScheduleSettingsForecastDailyNONDSD.pending, (state) => {
        state.scheduleSetting.forecastDailyNONDSDLoading = true;
        state.scheduleSetting.loading = true;
        state.scheduleSetting.error = null;
      })
      .addCase(fetchScheduleSettingsForecastDailyNONDSD.fulfilled, (state, action) => {
        state.scheduleSetting.forecastDailyNONDSDLoading = false;
        state.scheduleSetting.loading = false;
        if (action.payload !== null) {
          state.scheduleSetting.scheduleSettingsForecastDailyNONDSD = action.payload;
        }
        // If payload is null, it means there was an error handled centrally
      })
      .addCase(fetchScheduleSettingsForecastDailyNONDSD.rejected, (state, action) => {
        state.scheduleSetting.forecastDailyNONDSDLoading = false;
        state.scheduleSetting.error = action.payload as string || 'Failed to fetch Schedule Settings Forecast Daily NON-DSD';
        state.scheduleSetting.loading = false;
      })
      // Schedule Settings Actual Daily
      .addCase(fetchScheduleSettingsActualDaily.pending, (state) => {
        state.scheduleSetting.actualDailyLoading = true;
        state.scheduleSetting.loading = true;
        state.scheduleSetting.error = null;
      })
      .addCase(fetchScheduleSettingsActualDaily.fulfilled, (state, action) => {
        state.scheduleSetting.actualDailyLoading = false;
        state.scheduleSetting.scheduleSettingsActualDaily = action.payload;
        state.scheduleSetting.loading = false;
      })
      .addCase(fetchScheduleSettingsActualDaily.rejected, (state, action) => {
        state.scheduleSetting.actualDailyLoading = false;
        state.scheduleSetting.error = action.payload as string || 'Failed to fetch Schedule Settings Actual Daily';
        state.scheduleSetting.loading = false;
      })
      // Schedule Settings Actual Daily NON-DSD
      .addCase(fetchScheduleSettingsActualDailyNONDSD.pending, (state) => {
        state.scheduleSetting.actualDailyNONDSDLoading = true;
        state.scheduleSetting.loading = true;
        state.scheduleSetting.error = null;
      })
      .addCase(fetchScheduleSettingsActualDailyNONDSD.fulfilled, (state, action) => {
        state.scheduleSetting.actualDailyNONDSDLoading = false;
        state.scheduleSetting.scheduleSettingsActualDailyNONDSD = action.payload;
        state.scheduleSetting.loading = false;
      })
      .addCase(fetchScheduleSettingsActualDailyNONDSD.rejected, (state, action) => {
        state.scheduleSetting.actualDailyNONDSDLoading = false;
        state.scheduleSetting.error = action.payload as string || 'Failed to fetch Schedule Settings Actual Daily NON-DSD';
        state.scheduleSetting.loading = false;
      })
      // Update Schedule Settings Forecast Daily
      .addCase(updateScheduleSettingsForecastDaily.pending, (state, action) => {
        // Set individual loading state based on type
        if (action.meta.arg.type === 'DSD') {
          state.scheduleSetting.forecastDSDUpdateLoading = true;
        } else {
          state.scheduleSetting.forecastNONDSDUpdateLoading = true;
        }
        state.scheduleSetting.updateLoading = true;
        state.scheduleSetting.updateSuccess = false;
        state.scheduleSetting.error = null;
      })
      .addCase(updateScheduleSettingsForecastDaily.fulfilled, (state, action) => {
        // Reset individual loading state based on type
        if (action.meta.arg.type === 'DSD') {
          state.scheduleSetting.forecastDSDUpdateLoading = false;
        } else {
          state.scheduleSetting.forecastNONDSDUpdateLoading = false;
        }
        state.scheduleSetting.updateLoading = false;
        state.scheduleSetting.updateSuccess = true;
        state.scheduleSetting.error = null;
      })
      .addCase(updateScheduleSettingsForecastDaily.rejected, (state, action) => {
        // Reset individual loading state based on type
        if (action.meta.arg.type === 'DSD') {
          state.scheduleSetting.forecastDSDUpdateLoading = false;
        } else {
          state.scheduleSetting.forecastNONDSDUpdateLoading = false;
        }
        state.scheduleSetting.updateLoading = false;
        state.scheduleSetting.updateSuccess = false;
        state.scheduleSetting.error = action.payload as string || 'Failed to update Schedule Settings Forecast Daily';
      })
      // Update Schedule Settings Actual Daily
      .addCase(updateScheduleSettingsActualDaily.pending, (state, action) => {
        // Set individual loading state based on type
        if (action.meta.arg.type === 'DSD') {
          state.scheduleSetting.actualDSDUpdateLoading = true;
        } else {
          state.scheduleSetting.actualNONDSDUpdateLoading = true;
        }
        state.scheduleSetting.updateLoading = true;
        state.scheduleSetting.updateSuccess = false;
        state.scheduleSetting.error = null;
      })
      .addCase(updateScheduleSettingsActualDaily.fulfilled, (state, action) => {
        // Reset individual loading state based on type
        if (action.meta.arg.type === 'DSD') {
          state.scheduleSetting.actualDSDUpdateLoading = false;
        } else {
          state.scheduleSetting.actualNONDSDUpdateLoading = false;
        }
        state.scheduleSetting.updateLoading = false;
        state.scheduleSetting.updateSuccess = true;
        state.scheduleSetting.error = null;
      })
      .addCase(updateScheduleSettingsActualDaily.rejected, (state, action) => {
        // Reset individual loading state based on type
        if (action.meta.arg.type === 'DSD') {
          state.scheduleSetting.actualDSDUpdateLoading = false;
        } else {
          state.scheduleSetting.actualNONDSDUpdateLoading = false;
        }
        state.scheduleSetting.updateLoading = false;
        state.scheduleSetting.updateSuccess = false;
        state.scheduleSetting.error = action.payload as string || 'Failed to update Schedule Settings Actual Daily';
      })
      // Schedule Settings Forecast Zone Distribution DSD
      .addCase(fetchScheduleSettingsForecastZoneDistributionDSD.pending, (state) => {
        state.scheduleSetting.forecastZoneDistributionDSDLoading = true;
        state.scheduleSetting.loading = true;
        state.scheduleSetting.error = null;
      })
      .addCase(fetchScheduleSettingsForecastZoneDistributionDSD.fulfilled, (state, action) => {
        state.scheduleSetting.forecastZoneDistributionDSDLoading = false;
        state.scheduleSetting.scheduleSettingsForecastZoneDistributionDSD = Array.isArray(action.payload)
          ? { data: action.payload }
          : action.payload;
        state.scheduleSetting.loading = false;
      })
      .addCase(fetchScheduleSettingsForecastZoneDistributionDSD.rejected, (state, action) => {
        state.scheduleSetting.forecastZoneDistributionDSDLoading = false;
        state.scheduleSetting.error = action.payload as string || 'Failed to fetch Schedule Settings Forecast Zone Distribution DSD';
        state.scheduleSetting.loading = false;
      })
      // Schedule Settings Forecast Zone Distribution NON-DSD
      .addCase(fetchScheduleSettingsForecastZoneDistributionNONDSD.pending, (state) => {
        state.scheduleSetting.forecastZoneDistributionNONDSDLoading = true;
        state.scheduleSetting.loading = true;
        state.scheduleSetting.error = null;
      })
      .addCase(fetchScheduleSettingsForecastZoneDistributionNONDSD.fulfilled, (state, action) => {
        state.scheduleSetting.forecastZoneDistributionNONDSDLoading = false;
        state.scheduleSetting.scheduleSettingsForecastZoneDistributionNONDSD = Array.isArray(action.payload)
          ? { data: action.payload }
          : action.payload;
        state.scheduleSetting.loading = false;
      })
      .addCase(fetchScheduleSettingsForecastZoneDistributionNONDSD.rejected, (state, action) => {
        state.scheduleSetting.forecastZoneDistributionNONDSDLoading = false;
        state.scheduleSetting.error = action.payload as string || 'Failed to fetch Schedule Settings Forecast Zone Distribution NON-DSD';
        state.scheduleSetting.loading = false;
      })
      // Schedule Settings Actual Zone Distribution DSD
      .addCase(fetchScheduleSettingsActualZoneDistributionDSD.pending, (state) => {
        state.scheduleSetting.actualZoneDistributionDSDLoading = true;
        state.scheduleSetting.loading = true;
        state.scheduleSetting.error = null;
      })
      .addCase(fetchScheduleSettingsActualZoneDistributionDSD.fulfilled, (state, action) => {
        state.scheduleSetting.actualZoneDistributionDSDLoading = false;
        state.scheduleSetting.scheduleSettingsActualZoneDistributionDSD = Array.isArray(action.payload)
          ? { data: action.payload }
          : action.payload;
        state.scheduleSetting.loading = false;
      })
      .addCase(fetchScheduleSettingsActualZoneDistributionDSD.rejected, (state, action) => {
        state.scheduleSetting.actualZoneDistributionDSDLoading = false;
        state.scheduleSetting.error = action.payload as string || 'Failed to fetch Schedule Settings Actual Zone Distribution DSD';
        state.scheduleSetting.loading = false;
      })
      // Schedule Settings Actual Zone Distribution NON-DSD
      .addCase(fetchScheduleSettingsActualZoneDistributionNONDSD.pending, (state) => {
        state.scheduleSetting.actualZoneDistributionNONDSDLoading = true;
        state.scheduleSetting.loading = true;
        state.scheduleSetting.error = null;
      })
      .addCase(fetchScheduleSettingsActualZoneDistributionNONDSD.fulfilled, (state, action) => {
        state.scheduleSetting.actualZoneDistributionNONDSDLoading = false;
        state.scheduleSetting.scheduleSettingsActualZoneDistributionNONDSD = Array.isArray(action.payload)
          ? { data: action.payload }
          : action.payload;
        state.scheduleSetting.loading = false;
      })
      .addCase(fetchScheduleSettingsActualZoneDistributionNONDSD.rejected, (state, action) => {
        state.scheduleSetting.actualZoneDistributionNONDSDLoading = false;
        state.scheduleSetting.error = action.payload as string || 'Failed to fetch Schedule Settings Actual Zone Distribution NON-DSD';
        state.scheduleSetting.loading = false;
      })
      // Schedule Settings Historical Hours
      .addCase(fetchScheduleSettingsHistoricalHours.pending, (state) => {
        state.scheduleSetting.scheduleSettingsHistoricalHoursLoading = true;
        state.scheduleSetting.loading = true;
        state.scheduleSetting.error = null;
      })
      .addCase(fetchScheduleSettingsHistoricalHours.fulfilled, (state, action) => {
        state.scheduleSetting.scheduleSettingsHistoricalHoursLoading = false;
        state.scheduleSetting.scheduleSettingsHistoricalHours = action.payload;
        state.scheduleSetting.loading = false;
      })
      .addCase(fetchScheduleSettingsHistoricalHours.rejected, (state, action) => {
        state.scheduleSetting.scheduleSettingsHistoricalHoursLoading = false;
        state.scheduleSetting.error = action.payload as string || 'Failed to fetch Schedule Settings Historical Hours';
        state.scheduleSetting.loading = false;
      })
      // Update Schedule Settings Forecast Zone Distribution
      .addCase(updateScheduleSettingsForecastZoneDistribution.pending, (state, action) => {
        // Set individual loading state based on type
        if (action.meta.arg.type === 'DSD') {
          state.scheduleSetting.forecastDSDZoneUpdateLoading = true;
        } else {
          state.scheduleSetting.forecastNONDSDZoneUpdateLoading = true;
        }
        state.scheduleSetting.updateLoading = true;
        state.scheduleSetting.updateSuccess = false;
        state.scheduleSetting.error = null;
      })
      .addCase(updateScheduleSettingsForecastZoneDistribution.fulfilled, (state, action) => {
        // Reset individual loading state and set saved state based on type
        if (action.meta.arg.type === 'DSD') {
          state.scheduleSetting.forecastDSDZoneUpdateLoading = false;
          state.scheduleSetting.forecastDSDZoneSaved = true;
        } else {
          state.scheduleSetting.forecastNONDSDZoneUpdateLoading = false;
          state.scheduleSetting.forecastNONDSDZoneSaved = true;
        }
        state.scheduleSetting.updateLoading = false;
        state.scheduleSetting.updateSuccess = true;
        state.scheduleSetting.error = null;
      })
      .addCase(updateScheduleSettingsForecastZoneDistribution.rejected, (state, action) => {
        // Reset individual loading state based on type
        if (action.meta.arg.type === 'DSD') {
          state.scheduleSetting.forecastDSDZoneUpdateLoading = false;
        } else {
          state.scheduleSetting.forecastNONDSDZoneUpdateLoading = false;
        }
        state.scheduleSetting.updateLoading = false;
        state.scheduleSetting.updateSuccess = false;
        state.scheduleSetting.error = action.payload as string || 'Failed to update Schedule Settings Forecast Zone Distribution';
      })
      // Update Schedule Settings Actual Zone Distribution
      .addCase(updateScheduleSettingsActualZoneDistribution.pending, (state, action) => {
        // Set individual loading state based on type
        if (action.meta.arg.type === 'DSD') {
          state.scheduleSetting.actualDSDZoneUpdateLoading = true;
        } else {
          state.scheduleSetting.actualNONDSDZoneUpdateLoading = true;
        }
        state.scheduleSetting.updateLoading = true;
        state.scheduleSetting.updateSuccess = false;
        state.scheduleSetting.error = null;
      })
      .addCase(updateScheduleSettingsActualZoneDistribution.fulfilled, (state, action) => {
        // Reset individual loading state and set saved state based on type
        if (action.meta.arg.type === 'DSD') {
          state.scheduleSetting.actualDSDZoneUpdateLoading = false;
          state.scheduleSetting.actualDSDZoneSaved = true;
        } else {
          state.scheduleSetting.actualNONDSDZoneUpdateLoading = false;
          state.scheduleSetting.actualNONDSDZoneSaved = true;
        }
        state.scheduleSetting.updateLoading = false;
        state.scheduleSetting.updateSuccess = true;
        state.scheduleSetting.error = null;
      })
      .addCase(updateScheduleSettingsActualZoneDistribution.rejected, (state, action) => {
        // Reset individual loading state based on type
        if (action.meta.arg.type === 'DSD') {
          state.scheduleSetting.actualDSDZoneUpdateLoading = false;
        } else {
          state.scheduleSetting.actualNONDSDZoneUpdateLoading = false;
        }
        state.scheduleSetting.updateLoading = false;
        state.scheduleSetting.updateSuccess = false;
        state.scheduleSetting.error = action.payload as string || 'Failed to update Schedule Settings Actual Zone Distribution';
      })
      // Update Schedule Settings Historical Hours
      .addCase(updateScheduleSettingsHistoricalHours.pending, (state) => {
        state.scheduleSetting.historicalHoursUpdateLoading = true;
        state.scheduleSetting.updateLoading = true;
        state.scheduleSetting.updateSuccess = false;
        state.scheduleSetting.error = null;
      })
      .addCase(updateScheduleSettingsHistoricalHours.fulfilled, (state, action) => {
        state.scheduleSetting.historicalHoursUpdateLoading = false;
        state.scheduleSetting.updateLoading = false;
        state.scheduleSetting.updateSuccess = true;
        state.scheduleSetting.error = null;
      })
      .addCase(updateScheduleSettingsHistoricalHours.rejected, (state, action) => {
        state.scheduleSetting.historicalHoursUpdateLoading = false;
        state.scheduleSetting.updateLoading = false;
        state.scheduleSetting.updateSuccess = false;
        state.scheduleSetting.error = action.payload as string || 'Failed to update Schedule Settings Historical Hours';
      })
      
      // Update Location Settings Historical Hours
      .addCase(updateLocationSettingsHistoricalHours.pending, (state) => {
        state.locationSetting.historicalHoursSaving = true;
        state.locationSetting.error = null;
        state.locationSetting.historicalHoursSaved = false;
      })
      .addCase(updateLocationSettingsHistoricalHours.fulfilled, (state, action) => {
        state.locationSetting.historicalHoursSaving = false;
        state.locationSetting.error = null;
        state.locationSetting.historicalHoursSaved = true;
      })
      .addCase(updateLocationSettingsHistoricalHours.rejected, (state, action) => {
        state.locationSetting.historicalHoursSaving = false;
        state.locationSetting.error = action.payload as string || 'Failed to update Location Settings Historical Hours';
        state.locationSetting.historicalHoursSaved = false;
      })
      
      // Schedule Forecast
      .addCase(fetchScheduleForecast.pending, (state) => {
        state.scheduleSetting.scheduleForecastLoading = true;
        state.scheduleSetting.scheduleForecastError = null;
      })
      .addCase(fetchScheduleForecast.fulfilled, (state, action) => {
        state.scheduleSetting.scheduleForecastLoading = false;
        state.scheduleSetting.scheduleForecast = action.payload;
        state.scheduleSetting.scheduleForecastError = null;
      })
      .addCase(fetchScheduleForecast.rejected, (state, action) => {
        state.scheduleSetting.scheduleForecastLoading = false;
        state.scheduleSetting.scheduleForecastError = action.payload as string || 'Failed to fetch Schedule Forecast';
      })

      // Clean Schedule Settings
      .addCase(cleanScheduleSettings.pending, (state) => {
        state.scheduleSetting.cleanScheduleSettingsLoading = true;
        state.scheduleSetting.cleanScheduleSettingsError = null;
      })
      .addCase(cleanScheduleSettings.fulfilled, (state, action) => {
        state.scheduleSetting.cleanScheduleSettingsLoading = false;
        state.scheduleSetting.cleanScheduleSettingsData = action.payload;
        state.scheduleSetting.cleanScheduleSettingsError = null;
      })
      .addCase(cleanScheduleSettings.rejected, (state, action) => {
        state.scheduleSetting.cleanScheduleSettingsLoading = false;
        state.scheduleSetting.cleanScheduleSettingsError = action.payload as string || 'Failed to clean schedule settings';
      })

      // Create Schedule Hours async thunk cases
      .addCase(fetchCreateScheduleHours.pending, (state) => {
        state.createSchedule.scheduleHoursLoading = true;
        state.createSchedule.scheduleHoursError = null;
      })
      .addCase(fetchCreateScheduleHours.fulfilled, (state, action) => {
        state.createSchedule.scheduleHoursLoading = false;
        state.createSchedule.scheduleHours = action.payload;
        state.createSchedule.scheduleHoursError = null;
      })
      .addCase(fetchCreateScheduleHours.rejected, (state, action) => {
        state.createSchedule.scheduleHoursLoading = false;
        state.createSchedule.scheduleHoursError = action.payload as string || 'Failed to fetch Create Schedule Hours';
      })
      // Actual Hours async thunk cases
      .addCase(fetchActualHours.pending, (state) => {
        state.createActualHours.actualHoursLoading = true;
        state.createActualHours.actualHoursError = null;
      })
      .addCase(fetchActualHours.fulfilled, (state, action) => {
        state.createActualHours.actualHoursLoading = false;
        state.createActualHours.actualHours = action.payload;
        state.createActualHours.actualHoursError = null;
      })
      .addCase(fetchActualHours.rejected, (state, action) => {
        state.createActualHours.actualHoursLoading = false;
        state.createActualHours.actualHoursError = action.payload as string || 'Failed to fetch Actual Hours';
      })
      // Schedule Employee Data async thunk cases
      .addCase(fetchScheduleEmployeeData.pending, (state) => {
        state.createSchedule.scheduleEmployeeDataLoading = true;
        state.createSchedule.scheduleEmployeeDataError = null;
      })
      .addCase(fetchScheduleEmployeeData.fulfilled, (state, action) => {
        state.createSchedule.scheduleEmployeeDataLoading = false;
        
        // Store the raw unfiltered schedule data for orphan detection during save
        const rawPayload = action.payload?.data || action.payload || [];
        state.createSchedule.initialSchedules = Array.isArray(rawPayload) ? [...rawPayload] : rawPayload;
        
        // Filter out employees where all days (1-7) have no schdHours or actualHours
        const rawData = action.payload?.data || action.payload || [];
        
        if (Array.isArray(rawData) && rawData.length > 0) {
          // Group data by unique employee key (employeeGpid + role + shift)
          const employeeGroups = new Map<string, any[]>();
          
          rawData.forEach((record: any) => {
            const key = `${record.employeeGpid}_${record.role}_${record.shift}`;
            if (!employeeGroups.has(key)) {
              employeeGroups.set(key, []);
            }
            employeeGroups.get(key)!.push(record);
          });
          
          // Filter out employee groups where all 7 days have no hours
          const filteredData: any[] = [];
          
          employeeGroups.forEach((records, key) => {
            // Check if at least one day has schdHours or actualHours > 0
            const hasAnyHours = records.some((record) => {
              const schdHours = record.schdHours || 0;
              const actualHours = record.actualHours || 0;
              const overrideActualHours = record.overrideActualHours || 0;
              return schdHours > 0 || actualHours > 0 || overrideActualHours > 0;
            });
            
            // Only include this employee's records if they have hours on at least one day
            if (hasAnyHours) {
              filteredData.push(...records);
            }
          });
          
          console.log(`Filtered ${rawData.length - filteredData.length} employee records with no hours across all days`);
          
          // Store filtered data
          state.createSchedule.scheduleEmployeeData = action.payload?.data ? { ...action.payload, data: filteredData } : filteredData;
        } else {
          state.createSchedule.scheduleEmployeeData = action.payload;
        }
        
        state.createSchedule.scheduleEmployeeDataError = null;
        state.createSchedule.hasUnsavedChanges = false;
      })
      .addCase(fetchScheduleEmployeeData.rejected, (state, action) => {
        state.createSchedule.scheduleEmployeeDataLoading = false;
        state.createSchedule.scheduleEmployeeDataError = action.payload as string || 'Failed to fetch Schedule Employee Data';
      })
      // Employee Roles
      .addCase(fetchEmployeeRoles.pending, (state) => {
        state.createSchedule.employeeRolesLoading = true;
        state.createSchedule.employeeRolesError = null;
      })
      .addCase(fetchEmployeeRoles.fulfilled, (state, action) => {
        state.createSchedule.employeeRolesLoading = false;
        state.createSchedule.employeeRoles = action.payload;
        state.createSchedule.employeeRolesError = null;
      })
      .addCase(fetchEmployeeRoles.rejected, (state, action) => {
        state.createSchedule.employeeRolesLoading = false;
        state.createSchedule.employeeRolesError = action.payload as string || 'Failed to fetch Employee Roles';
      })
      // All Employee Roles (including disabled) — for existing schedule edge case
      .addCase(fetchAllEmployeeRoles.fulfilled, (state, action) => {
        state.createSchedule.allEmployeeRoles = action.payload;
      })
      // Employees
      .addCase(fetchEmployees.pending, (state) => {
        state.createSchedule.employeesLoading = true;
        state.createSchedule.employeesError = null;
      })
      .addCase(fetchEmployees.fulfilled, (state, action) => {
        state.createSchedule.employeesLoading = false;
        state.createSchedule.employees = action.payload;
        state.createSchedule.employeesError = null;
      })
      .addCase(fetchEmployees.rejected, (state, action) => {
        state.createSchedule.employeesLoading = false;
        state.createSchedule.employeesError = action.payload as string || 'Failed to fetch Employees';
      })
      // Save Schedule Data async thunk cases
      .addCase(saveScheduleData.pending, (state) => {
        state.createSchedule.saveLoading = true;
        state.createSchedule.saveError = null;
        state.createSchedule.saveSuccess = false;
      })
      .addCase(saveScheduleData.fulfilled, (state, action) => {
        state.createSchedule.saveLoading = false;
        state.createSchedule.saveError = null;
        state.createSchedule.saveSuccess = true;
        state.createSchedule.hasUnsavedChanges = false;
      })
      .addCase(saveScheduleData.rejected, (state, action) => {
        state.createSchedule.saveLoading = false;
        state.createSchedule.saveError = action.payload as string || 'Failed to save schedule data';
        state.createSchedule.saveSuccess = false;
      })
      // Submit Schedule Status async thunk cases
      .addCase(submitScheduleStatus.pending, (state) => {
        state.createSchedule.submitLoading = true;
        state.createSchedule.submitError = null;
        state.createSchedule.submitSuccess = false;
      })
      .addCase(submitScheduleStatus.fulfilled, (state, action) => {
        state.createSchedule.submitLoading = false;
        state.createSchedule.submitError = null;
        state.createSchedule.submitSuccess = true;
        state.createSchedule.submitResponse = action.payload;
      })
      .addCase(submitScheduleStatus.rejected, (state, action) => {
        state.createSchedule.submitLoading = false;
        state.createSchedule.submitError = action.payload as string || 'Failed to submit schedule status';
        state.createSchedule.submitSuccess = false;
      })
      
      // Actual Volume Sync thunks - no state changes needed since they're handled internally
      .addCase('actualVolume/distributeWeeklyToZones/pending', (state) => {
        // Optional: add loading state if needed
      })
      .addCase('actualVolume/distributeWeeklyToZones/fulfilled', (state) => {
        // No additional state changes needed - handled by thunk
      })
      .addCase('actualVolume/distributeWeeklyToZones/rejected', (state) => {
        console.error('Distribution failed');
      })
      .addCase('actualVolume/recalculateWeeklyFromZones/pending', (state) => {
        // Optional: add loading state if needed
      })
      .addCase('actualVolume/recalculateWeeklyFromZones/fulfilled', (state) => {
        // No additional state changes needed - handled by thunk
      })
      .addCase('actualVolume/recalculateWeeklyFromZones/rejected', (state) => {
        console.error('Recalculation failed');
      });
  },
});

export const {
  // Centralized Error Handling Actions
  addCentralizedError,
  setActiveError,
  showErrorModal,
  hideErrorModal,
  clearAllErrors,
  removeErrorById,
  
  // Dashboard actions
  setDashboardLoading,
  setDashboardError,
  setDashboardData,
  setFilteredScheduleLoading,
  setFilteredScheduleError,
  setFilteredScheduleData,
  setKCPHLoading,
  setKCPHError,
  setKCPHData,
  setHoursLoading,
  setHoursError,
  setHoursData,
  setScheduleLoading,
  setScheduleError,
  setScheduleData,
  setLocationSettingLoading,
  setLocationSettingError,
  setLocationSetting,
  setScheduleSettingLoading,
  setScheduleSettingError,
  setScheduleSettingsForecastDaily,
  setScheduleSettingsForecastDailyNONDSD,
  resetScheduleSettingUpdateState,
  resetSaveState,
  resetScheduleSettingData,
  setForecastDSDTableData,
  setForecastNONDSDTableData,
  setActualDSDTableData,
  setActualNONDSDTableData,
  setForecastDSDZoneTableData,
  setForecastNONDSDZoneTableData,
  setActualDSDZoneTableData,
  setActualNONDSDZoneTableData,
  setHistoricalHoursTableData,
  setHistoricalHoursUnsavedChanges,
  setActiveContext,
  setLocationHistoricalHoursSaved,
  setShiftTimesLoading,
  setShiftTimesData,
  setShiftTimesTableData,
  setShiftTimesUpdateLoading,
  setShiftTimesError,
  setScheduleForecastLoading,
  setScheduleForecastData,
  setScheduleForecastTableData,
  setScheduleForecastError,
  setCleanScheduleSettingsLoading,
  setCleanScheduleSettingsData,
  setCleanScheduleSettingsError,
  setCreateScheduleLoading,
  setCreateScheduleError,
  setCreateScheduleData,
  setCreateSchedulePeriodWeek,
  clearCreateSchedulePeriodWeek,
  addEmployeeRow,
  updateEmployeeRow,
  removeEmployeeRow,
  clearAllEmployeeRows,
  clearScheduleEmployeeData,
  updateScheduleHours,
  updateActualHours,
  splitEmployeeRole,
  splitRoleAfterSibling,
  removeSplitRole,
  setFilteredRoles,
  toggleSelectedDay,
  setSelectedDays,
  clearSelectedDays,
  setDayFilter,
  // Actual Hours actions
  setCreateActualHoursLoading,
  setCreateActualHoursError,
  setCreateActualHoursData,
  setCreateActualHoursPeriodWeek,
  clearCreateActualHoursPeriodWeek,
  addActualEmployeeRow,
  updateActualEmployeeRow,
  removeActualEmployeeRow,
  clearAllActualEmployeeRows,
  splitActualEmployeeRole,
  splitActualRoleAfterSibling,
  removeActualSplitRole,
  setActualFilteredRoles,
  toggleActualSelectedDay,
  setActualSelectedDays,
  clearActualSelectedDays,
  setActualDayFilter,
  copyScheduleToActualHours,
} = dloSlice.actions;

// Selectors for individual table loading states
export const selectForecastDSDUpdateLoading = (state: any) => state?.dlo?.scheduleSetting?.forecastDSDUpdateLoading || false;
export const selectForecastNONDSDUpdateLoading = (state: any) => state?.dlo?.scheduleSetting?.forecastNONDSDUpdateLoading || false;
export const selectActualDSDUpdateLoading = (state: any) => state?.dlo?.scheduleSetting?.actualDSDUpdateLoading || false;
export const selectActualNONDSDUpdateLoading = (state: any) => state?.dlo?.scheduleSetting?.actualNONDSDUpdateLoading || false;

// Selectors for zone distribution update loading states
export const selectForecastDSDZoneUpdateLoading = (state: any) => state?.dlo?.scheduleSetting?.forecastDSDZoneUpdateLoading || false;
export const selectForecastNONDSDZoneUpdateLoading = (state: any) => state?.dlo?.scheduleSetting?.forecastNONDSDZoneUpdateLoading || false;
export const selectActualDSDZoneUpdateLoading = (state: any) => state?.dlo?.scheduleSetting?.actualDSDZoneUpdateLoading || false;
export const selectActualNONDSDZoneUpdateLoading = (state: any) => state?.dlo?.scheduleSetting?.actualNONDSDZoneUpdateLoading || false;

// Selectors for zone distribution saved states
export const selectForecastDSDZoneSaved = (state: any) => state?.dlo?.scheduleSetting?.forecastDSDZoneSaved || false;
export const selectForecastNONDSDZoneSaved = (state: any) => state?.dlo?.scheduleSetting?.forecastNONDSDZoneSaved || false;
export const selectActualDSDZoneSaved = (state: any) => state?.dlo?.scheduleSetting?.actualDSDZoneSaved || false;
export const selectActualNONDSDZoneSaved = (state: any) => state?.dlo?.scheduleSetting?.actualNONDSDZoneSaved || false;

// Selectors for historical hours
export const selectScheduleSettingsHistoricalHours = (state: any) => state?.dlo?.scheduleSetting?.scheduleSettingsHistoricalHours || null;
export const selectScheduleSettingsHistoricalHoursLoading = (state: any) => state?.dlo?.scheduleSetting?.scheduleSettingsHistoricalHoursLoading || false;
export const selectHistoricalHoursUpdateLoading = (state: any) => state?.dlo?.scheduleSetting?.historicalHoursUpdateLoading || false;
export const selectHistoricalHoursTableData = (state: any) => state?.dlo?.scheduleSetting?.historicalHoursTableData || null;
export const selectHistoricalHoursUnsavedChanges = (state: any) => state?.dlo?.scheduleSetting?.historicalHoursUnsavedChanges || false;

// Selectors for location setting historical hours
export const selectLocationHistoricalHoursSaved = (state: any) => state?.dlo?.locationSetting?.historicalHoursSaved || false;
export const selectLocationHistoricalHoursSaving = (state: any) => state?.dlo?.locationSetting?.historicalHoursSaving || false;

// Selectors for shift times
export const selectShiftTimes = (state: any) => state?.dlo?.scheduleSetting?.shiftTimes || null;
export const selectShiftTimesLoading = (state: any) => state?.dlo?.scheduleSetting?.shiftTimesLoading || false;
export const selectShiftTimesTableData = (state: any) => state?.dlo?.scheduleSetting?.shiftTimesTableData || null;
export const selectShiftTimesUpdateLoading = (state: any) => state?.dlo?.scheduleSetting?.shiftTimesUpdateLoading || false;

// Selectors for schedule forecast
export const selectScheduleForecast = (state: any) => state?.dlo?.scheduleSetting?.scheduleForecast || null;
export const selectScheduleForecastLoading = (state: any) => state?.dlo?.scheduleSetting?.scheduleForecastLoading || false;
export const selectScheduleForecastTableData = (state: any) => state?.dlo?.scheduleSetting?.scheduleForecastTableData || null;
export const selectScheduleForecastError = (state: any) => state?.dlo?.scheduleSetting?.scheduleForecastError || null;

// Selectors for clean schedule settings
export const selectCleanScheduleSettingsData = (state: any) => state?.dlo?.scheduleSetting?.cleanScheduleSettingsData || null;
export const selectCleanScheduleSettingsLoading = (state: any) => state?.dlo?.scheduleSetting?.cleanScheduleSettingsLoading || false;
export const selectCleanScheduleSettingsError = (state: any) => state?.dlo?.scheduleSetting?.cleanScheduleSettingsError || null;

// Selectors for createSchedule period, week, and year
export const selectCreateSchedulePeriod = (state: any) => state?.dlo?.createSchedule?.period || null;
export const selectCreateScheduleWeek = (state: any) => state?.dlo?.createSchedule?.week || null;
export const selectCreateScheduleYear = (state: any) => state?.dlo?.createSchedule?.year || null;

// Selectors for createSchedule hours data
export const selectCreateScheduleHours = (state: any) => state?.dlo?.createSchedule?.scheduleHours || null;
export const selectCreateScheduleHoursLoading = (state: any) => state?.dlo?.createSchedule?.scheduleHoursLoading || false;
export const selectCreateScheduleHoursError = (state: any) => state?.dlo?.createSchedule?.scheduleHoursError || null;

// Selectors for actual hours
export const selectActualHours = (state: any) => state?.dlo?.createActualHours?.actualHours || null;
export const selectActualHoursLoading = (state: any) => state?.dlo?.createActualHours?.actualHoursLoading || false;
export const selectActualHoursError = (state: any) => state?.dlo?.createActualHours?.actualHoursError || null;

// Selectors for employee rows
export const selectCreateScheduleEmployeeRows = (state: any) => state?.dlo?.createSchedule?.employeeRows || [];
export const selectCreateScheduleDeletedEmployeeRows = (state: any) => state?.dlo?.createSchedule?.deletedEmployeeRows || [];
export const selectCreateScheduleNextEmployeeRowId = (state: any) => state?.dlo?.createSchedule?.nextEmployeeRowId || 0;

// Stable empty-array sentinel used as selector fallbacks.
// MUST be a module-level constant so every call returns the same reference.
// Using an inline `|| []` would return a new array literal on every selector
// invocation when the value is falsy — useSelector would see a changed reference,
// trigger a re-render, the Picker dropdown would receive new `items`, call setState
// during rendering, and cause React error #185 (Too many re-renders).
const _EMPTY_ARRAY: never[] = [];

// Selectors for filtered roles
export const selectFilteredRoles = (state: any) => state?.dlo?.createSchedule?.filteredRoles || _EMPTY_ARRAY;

// Selectors for employee roles
export const selectEmployeeRoles = (state: any) => state?.dlo?.createSchedule?.employeeRoles || _EMPTY_ARRAY;
export const selectAllEmployeeRoles = (state: any) => state?.dlo?.createSchedule?.allEmployeeRoles || state?.dlo?.createSchedule?.employeeRoles || _EMPTY_ARRAY;
export const selectEmployeeRolesLoading = (state: any) => state?.dlo?.createSchedule?.employeeRolesLoading || false;
export const selectEmployeeRolesError = (state: any) => state?.dlo?.createSchedule?.employeeRolesError || null;

// Selectors for employees
export const selectEmployees = (state: any) => state?.dlo?.createSchedule?.employees || _EMPTY_ARRAY;
export const selectEmployeesLoading = (state: any) => state?.dlo?.createSchedule?.employeesLoading || false;
export const selectEmployeesError = (state: any) => state?.dlo?.createSchedule?.employeesError || null;

// Selectors for schedule employee data
export const selectScheduleEmployeeData = (state: any) => state?.dlo?.createSchedule?.scheduleEmployeeData || null;
export const selectScheduleEmployeeDataLoading = (state: any) => state?.dlo?.createSchedule?.scheduleEmployeeDataLoading || false;
export const selectScheduleEmployeeDataError = (state: any) => state?.dlo?.createSchedule?.scheduleEmployeeDataError || null;

// Selector for raw initial schedules (unfiltered, for orphan detection)
export const selectInitialSchedules = (state: any) => state?.dlo?.createSchedule?.initialSchedules || null;

// Selectors for save operation
export const selectSaveLoading = (state: any) => state?.dlo?.createSchedule?.saveLoading || false;
export const selectSaveError = (state: any) => state?.dlo?.createSchedule?.saveError || null;
export const selectSaveSuccess = (state: any) => state?.dlo?.createSchedule?.saveSuccess || false;
export const selectHasUnsavedChanges = (state: any) => state?.dlo?.createSchedule?.hasUnsavedChanges || false;

// Selectors for day filters
export const selectSelectedDays = (state: any) => state?.dlo?.createSchedule?.selectedDays || {};
export const selectDayFilter = (state: any) => state?.dlo?.createSchedule?.dayFilter || null;

// Selectors for createActualHours period, week, and year
export const selectCreateActualHoursPeriod = (state: any) => state?.dlo?.createActualHours?.period || null;
export const selectCreateActualHoursWeek = (state: any) => state?.dlo?.createActualHours?.week || null;
export const selectCreateActualHoursYear = (state: any) => state?.dlo?.createActualHours?.year || null;

// Selectors for createActualHours hours data
export const selectCreateActualHours = (state: any) => state?.dlo?.createActualHours?.actualHours || null;
export const selectCreateActualHoursLoading = (state: any) => state?.dlo?.createActualHours?.actualHoursLoading || false;
export const selectCreateActualHoursError = (state: any) => state?.dlo?.createActualHours?.actualHoursError || null;

// Selectors for actual employee rows
export const selectCreateActualHoursEmployeeRows = (state: any) => state?.dlo?.createActualHours?.actualEmployeeRows || [];
export const selectCreateActualHoursDeletedEmployeeRows = (state: any) => state?.dlo?.createActualHours?.actualDeletedEmployeeRows || [];
export const selectCreateActualHoursNextEmployeeRowId = (state: any) => state?.dlo?.createActualHours?.nextActualEmployeeRowId || 0;

// Selectors for actual filtered roles
export const selectActualFilteredRoles = (state: any) => state?.dlo?.createActualHours?.actualFilteredRoles || [];

// Selectors for actual day filters
export const selectActualSelectedDays = (state: any) => state?.dlo?.createActualHours?.actualSelectedDays || {};
export const selectActualDayFilter = (state: any) => state?.dlo?.createActualHours?.actualDayFilter || null;

// Selectors for KCPH data
export const selectKCPHData = (state: any) => state?.dlo?.dashboard?.kcphData || null;
export const selectKCPHLoading = (state: any) => state?.dlo?.dashboard?.kcphLoading || false;
export const selectKCPHError = (state: any) => state?.dlo?.dashboard?.kcphError || null;

// Selectors for Dashboard Hours data
export const selectHoursData = (state: any) => state?.dlo?.dashboard?.hoursData || null;
export const selectHoursLoading = (state: any) => state?.dlo?.dashboard?.hoursLoading || false;
export const selectHoursError = (state: any) => state?.dlo?.dashboard?.hoursError || null;

// Selectors for Dashboard Schedule data
export const selectScheduleData = (state: any) => state?.dlo?.dashboard?.scheduleData || null;
export const selectScheduleLoading = (state: any) => state?.dlo?.dashboard?.scheduleLoading || false;
export const selectScheduleError = (state: any) => state?.dlo?.dashboard?.scheduleError || null;

// Selectors for filtered schedule
export const selectFilteredScheduleData = (state: any) => state?.dlo?.dashboard?.filteredScheduleData || null;
export const selectFilteredScheduleLoading = (state: any) => state?.dlo?.dashboard?.filteredScheduleLoading || false;
export const selectFilteredScheduleError = (state: any) => state?.dlo?.dashboard?.filteredScheduleError || null;

// Selectors for submit functionality
export const selectSubmitLoading = (state: any) => state?.dlo?.createSchedule?.submitLoading || false;
export const selectSubmitError = (state: any) => state?.dlo?.createSchedule?.submitError || null;
export const selectSubmitSuccess = (state: any) => state?.dlo?.createSchedule?.submitSuccess || false;
export const selectSubmitResponse = (state: any) => state?.dlo?.createSchedule?.submitResponse || null;

export default dloSlice.reducer;
